import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import api from "../services/api";
import { todayLocalISO } from "../utils/date";
import { LogoContext } from "../context/LogoContext";
import useSessionTimer from "../hooks/useSessionTimer";
import { useConfirmDialog } from "../hooks/useConfirmDialog";
import { useDashboardData, CHART_WEEKS } from "../hooks/useDashboardData";
import { useWorkoutSession } from "../hooks/useWorkoutSession";
import { useCheckin } from "../hooks/useCheckin";
import { useTheme } from "../hooks/useTheme";
import { usePaidAccess } from "../hooks/usePaidAccess";
import { useProfessorBillingAccess } from "../hooks/useProfessorBillingAccess";
import { useIsNarrow } from "../hooks/useMediaQuery";
import ActionMenu from "../components/ActionMenu";
import SunIcon from "../components/icons/SunIcon";
import CheckinsTimeline from "../components/CheckinsTimeline";
import TeacherAnalyticsDashboard from "../components/TeacherAnalyticsDashboard";
import AdminDashboard from "../components/AdminDashboard";
import RestBanner from "../components/RestBanner";
import PlusBadge from "../components/PlusBadge";
import InstallPwaModal from "../components/InstallPwaModal";
import EnablePushPrompt from "../components/EnablePushPrompt";
import { usePwaInstall } from "../hooks/usePwaInstall";
import { logPwaEvent, prefetchPushPublicKey, subscribeToPush } from "../utils/pwa";
import WorkoutSummaryModal from "../components/WorkoutSummaryModal";
import RestConflictModal from "../components/RestConflictModal";
import { ActivityRings, DashboardSkeleton, Card, PointsCard, ProgressBar, Modal } from "../components/DashboardUI";
import { getUserLevel } from "../utils/levels";
import { getCurrentPositionSafe } from "../utils/geolocation";
import styles from "./Dashboard.module.css";
import UserAdminModal from "../components/UserAdminModal";
import { useToast } from "../components/ToastProvider";
import ProfileModal from "../components/ProfileModal";
import WorkoutAdminModal from "../components/WorkoutAdminModal";
import TeacherDashboard from "../components/TeacherDashboard";
import BodyMeasurementsModal from "../components/BodyMeasurementsModal";
import NutritionModal from "../components/NutritionModal";
import ChatModal from "../components/ChatModal";
import { useChatEntry } from "../hooks/useChatEntry";
import DietBannerCard from "../components/DietBannerCard";
import SessionSummaryButton from "../components/SessionSummaryButton";
import FeedbackInboxModal from "../components/FeedbackInboxModal";
import WorkoutSetsTracker from "../components/WorkoutSetsTracker";
import GroupSetsTracker from "../components/GroupSetsTracker";
import GoogleWelcome from "../components/GoogleWelcome";
import ConfirmDialog from "../components/ConfirmDialog";
import CardioCheckinModal from "../components/CardioCheckinModal";
import ChallengeManageModal from "../components/ChallengeManageModal";
import StreakCard from "../components/StreakCard";
import WeeklyVolumeChart from "../components/WeeklyVolumeChart";
import WeeklyLoadChart from "../components/WeeklyLoadChart";
import ExerciseDemoModal from "../components/ExerciseDemoModal";
import SessionDetailModal from "../components/SessionDetailModal";
import WeatherPanel from "../components/WeatherPanel";
import AiUsageModal from "../components/AiUsageModal";
import SendEmailModal from "../components/SendEmailModal";
import AdminSendPushModal from "../components/AdminSendPushModal";
import ChallengeResultsModal from "../components/ChallengeResultsModal";
import FriendChallengeModal from "../components/FriendChallengeModal";
import SplitDateInput from "../components/SplitDateInput";
import SelfWorkoutModal from "../components/SelfWorkoutModal";
import MigrateWorkoutModal from "../components/MigrateWorkoutModal";
import SelfInsightsModal from "../components/SelfInsightsModal";
import StudentReportModal from "../components/StudentReportModal";
import WorkoutRationaleModal from "../components/WorkoutRationaleModal";
import PaymentsModal from "../components/PaymentsModal";
import PaymentsAdminModal from "../components/PaymentsAdminModal";
import ProfessorBillingModal from "../components/ProfessorBillingModal";
import OrganizationModal from "../components/OrganizationModal";
import OrganizationsAdminModal from "../components/OrganizationsAdminModal";
import ProfessorCheckoutModal from "../components/ProfessorCheckoutModal";
import InviteProfessorModal from "../components/InviteProfessorModal";
import { applyProfessorTheme, applyPwaManifest } from "../utils/theme";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars, faMoon,
  faChevronRight,
  faFireFlameCurved,
  faArrowTrendUp,
  faCheckCircle,
  faTriangleExclamation,
  faPenToSquare,
  faPen,
  faTrashCan,
  faClock,
  faChartBar,
  faChartLine,
  faMedal,
  faTrophy,
  faRightFromBracket,
  faDumbbell,
  faStopwatch,
  faUtensils,
  faPersonRunning,
  faCommentDots,
  faComments,
  faPlay,
  faEye,
  faRobot,
  faEnvelopeOpenText,
  faFlagCheckered,
  faWandMagicSparkles,
  faLightbulb,
  faCheck,
  faLock,
  faCircleInfo,
  faPlus,
  faCreditCard,
  faCrown,
  faUserGroup,
  faBuilding,
  faBellSlash,
  faBell,
  faGlobe,
  faLink,
  faChalkboardUser,
} from "@fortawesome/free-solid-svg-icons";

const cn = (...classes) => classes.filter(Boolean).join(" ");


// Bi-set/superset/circuito (ver group_id em app/models/exercise.py) —
// transforma a lista plana de itens do treino (já ordenada por
// order_index) em "blocos" pra renderização: um item avulso OU um grupo
// contíguo de itens com o mesmo group_id. Mesma lógica usada no editor do
// professor (WorkoutAdminModal.jsx::buildItemBlocks) — o backend garante
// que itens de um grupo ficam sempre contíguos.
function buildRenderBlocks(items) {
  const blocks = [];
  let currentGroup = null;
  for (const item of items || []) {
    if (item.group_id != null && currentGroup && currentGroup.groupId === item.group_id) {
      currentGroup.items.push(item);
    } else if (item.group_id != null) {
      currentGroup = { kind: "group", groupId: item.group_id, items: [item] };
      blocks.push(currentGroup);
    } else {
      currentGroup = null;
      blocks.push({ kind: "item", item });
    }
  }
  return blocks;
}

function blockKeyOf(block) {
  return block.kind === "group" ? `group:${block.groupId}` : `item:${block.item.id}`;
}

// Rótulo do grupo (bi-set/superset/circuito) — mesma regra usada no editor
// do professor (WorkoutAdminModal.jsx::groupLabel), duplicada aqui porque
// são arquivos/bundles diferentes. 3+ exercícios = Circuito. Exatamente 2 =
// Bi-set quando é o MESMO exercise_id (mesmo grupo muscular, ex. supino +
// crucifixo = peito) ou Superset quando são grupos diferentes (ex. rosca +
// tríceps testa = bíceps x tríceps) — a diferença é o grupo muscular, não a
// quantidade de exercícios (as duas variantes são sempre pares).
function computeGroupLabel(items) {
  const size = items?.length ?? 0;
  if (size >= 3) return "Circuito";
  if (size === 2) {
    const [a, b] = items;
    const sameMuscleGroup = a?.exercise_id != null && a.exercise_id === b?.exercise_id;
    return sameMuscleGroup ? "Bi-set" : "Superset";
  }
  return "Bi-set";
}

function getGreeting() {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return "Bom dia";
  if (h >= 12 && h < 18) return "Boa tarde";
  return "Boa noite";
}

function formatHeaderDate() {
  const now = new Date();
  const weekdays = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
  const months   = ["jan","fev","mar","abr","mai","jun","jul","ago","set","out","nov","dez"];
  return `${weekdays[now.getDay()]}, ${now.getDate()} ${months[now.getMonth()]}`;
}

// Feature flag removida — controlada por is_self_service no runtime

export default function Dashboard({ user, onLogout, onUserUpdate }) {
  const apiUrl = process.env.REACT_APP_API_URL;
  // Fonte unica de verdade — mesmo breakpoint (BP_MOBILE) usado nos CSS
  // Modules. Antes era um listener de `resize` local, que disparava a cada
  // pixel e divergia dos breakpoints do CSS (auditoria mobile, achado C1).
  const isMobile = useIsNarrow();


  const remainingTime = useSessionTimer(() => onLogout(true));
  const minutes = remainingTime ? Math.floor(remainingTime / 1000 / 60) : 0;
  const seconds = remainingTime ? Math.floor((remainingTime / 1000) % 60) : 0;

  const [showCreateModal, setShowCreateModal] = useState(false);
  const { confirmState, openConfirm, closeConfirm, runConfirm } = useConfirmDialog();
  const [showMenu, setShowMenu] = useState(false);
  const [showPaymentsModal, setShowPaymentsModal] = useState(false);
  const [showPaymentsAdminModal, setShowPaymentsAdminModal] = useState(false);
  const [showProfessorBilling, setShowProfessorBilling] = useState(false);
  const [showOrganization, setShowOrganization] = useState(false);
  const [showInviteProfessor, setShowInviteProfessor] = useState(false);
  // Lista de academias (admin). inviteProfessorKind: o convite abre como
  // professor autônomo ou já como Academia ("Nova academia"); orgsReloadKey
  // recarrega a lista quando o convite fecha.
  const [showOrganizationsAdmin, setShowOrganizationsAdmin] = useState(false);
  const [inviteProfessorKind, setInviteProfessorKind] = useState("professor");
  const [orgsReloadKey, setOrgsReloadKey] = useState(0);
  const [showProfessorCheckout, setShowProfessorCheckout] = useState(false);
  const [selectedProfessorPlan, setSelectedProfessorPlan] = useState(null);

  const handleOpenProfessorCheckout = useCallback((plan) => {
    setSelectedProfessorPlan(plan);
    setShowProfessorBilling(false);
    setShowProfessorCheckout(true);
  }, []);
  const [showRankingModal, setShowRankingModal] = useState(false);
  const [rankingTab, setRankingTab] = useState("weekly"); // weekly | monthly | all | challenge
  const [rankingChallengeId, setRankingChallengeId] = useState(null);
  const [challengeRanking, setChallengeRanking] = useState([]);
  const [loadingChallengeRanking, setLoadingChallengeRanking] = useState(false);
  const [weeklyRanking, setWeeklyRanking] = useState([]);
  const [monthlyRanking, setMonthlyRanking] = useState([]);
  const [rankingPillIdx, setRankingPillIdx] = useState(0);
  const [loadingPeriodRanking, setLoadingPeriodRanking] = useState(false);
  const [showChallengesModal, setShowChallengesModal] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [manageChallenge, setManageChallenge] = useState(null);
  const [, setSelectedExercise] = useState(null);
  const [selectedWorkoutAssignmentId, setSelectedWorkoutAssignmentId] = useState("");
  const [toast, setToast] = useState(null);
  const toastApi = useToast();
  const { theme, setTheme } = useTheme();
  const [editingDayId, setEditingDayId] = useState(null);
  // Modal de conflito de descanso (48h) ao trocar dia de treino — substitui
  // window.confirm() nativo. { assignmentId, dayOfWeek, message, conflicts } | null
  const [restConflict, setRestConflict] = useState(null);
  const [restConflictSaving, setRestConflictSaving] = useState(false);
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showAiUsageModal, setShowAiUsageModal] = useState(false);
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [showAdminPushModal, setShowAdminPushModal] = useState(false);
  const [resultsChallenge, setResultsChallenge] = useState(null);
  const [showFriendChallengeModal, setShowFriendChallengeModal] = useState(false);
  const [sessionDetail, setSessionDetail] = useState(null); // { sessionId, checkin }
  const [showCancelSessionConfirm, setShowCancelSessionConfirm] = useState(false);
  const [showFinishNowConfirm, setShowFinishNowConfirm] = useState(false);
  const [finishNowLoading, setFinishNowLoading] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [demoExerciseItem, setDemoExerciseItem] = useState(null);
  const [expandedNotes, setExpandedNotes] = useState(new Set());
  const {
    workoutSession, setWorkoutSession,
    activeRest, setActiveRest,
    workoutSummary, setWorkoutSummary,
    previousSets,
    elapsedSeconds,
    workoutModalPreStart, setWorkoutModalPreStart,
    workoutModalReadonly, setWorkoutModalReadonly,
    pendingViewAssignmentId, setPendingViewAssignmentId,
    parseActiveSession,
    clearWorkoutSession,
    completeExercise,
    allExercisesCompleted,
    updateSetLog,
    formatElapsed,
  } = useWorkoutSession({ setToast });

  const getChallengeId = (c) => c.challenge_id ?? c.id;

  // ── Dados do dashboard (estado de fetch centralizado) ────────────────────────
  const {
    loading, loadError,
    me, setMe,
    totalPoints, setTotalPoints,
    pointsHistory,
    myChallenges,
    allChallenges,
    ranking,
    exercises,
    pointsToday,
    checkinsToday,
    checkins,
    assignedWorkouts, setAssignedWorkouts,
    weeklyCadenceSeries,
    weeklyDurationSeries,
    weeklyVolumeSeries,
    unreadFeedbacks, setUnreadFeedbacks,
    hasDiet, refetchHasDiet,
    loadData,
  } = useDashboardData({
    userId: user?.id,
    onActiveSession: useCallback(
      (data) => setWorkoutSession(prev => parseActiveSession(data, prev)),
      [parseActiveSession, setWorkoutSession]
    ),
    setToast,
  });

  const chatEntry = useChatEntry(me);
  const { hasPaidAccess, refetch: refetchPaidAccess } = usePaidAccess(me);
  const {
    hasProfessorBillingAccess, hasStudentBillingManagementAccess, hasActivePlatformSubscription,
    lastSubscription, lastOneOffAccess, refetch: refetchProfessorBillingAccess,
  } = useProfessorBillingAccess(me);

  // Pagamento confirmado no PaymentsModal. Dono de academia: recarrega o
  // /users/me, que traz a situação da cobrança da academia (org_billing),
  // pra liberar a gestão na hora em vez de só no próximo login.
  const handlePaymentConfirmed = useCallback(async () => {
    refetchPaidAccess();
    if (!me?.org_role) return;
    try {
      const { data } = await api.get("/users/me");
      setMe(data);
      onUserUpdate?.(data);
    } catch { /* segue com o me atual; o próximo carregamento corrige */ }
  }, [refetchPaidAccess, me?.org_role, onUserUpdate, setMe]);
  // ─────────────────────────────────────────────────────────────────────────────

  // GET /nutrition/diets é gated por Plus no backend — só dispara aqui depois
  // que hasPaidAccess resolveu, pra não gerar um 403 desperdiçado a cada
  // carregamento do dashboard pra aluno self-service sem Plus (mesma condição
  // usada no DietBannerCard/menu logo abaixo). Ver hooks/useDashboardData.js.
  useEffect(() => {
    if (me?.role === "aluno" && (!me?.is_self_service || hasPaidAccess)) {
      refetchHasDiet();
    }
  }, [me?.role, me?.is_self_service, hasPaidAccess, refetchHasDiet]);

  // Shim: inicializa setsLog quando a sessão e o plano ficam disponíveis.
  // Fica aqui (e não no hook) para evitar dependência circular entre
  // useWorkoutSession (que fornece parseActiveSession) e useDashboardData
  // (que fornece assignedWorkouts).
  useEffect(() => {
    if (!workoutSession || !selectedWorkoutAssignmentId) return;
    const assignment = assignedWorkouts.find(
      (a) => String(a.id) === String(selectedWorkoutAssignmentId)
    );
    if (!assignment) return;
    const items = (assignment.items || []).filter((it) => it.sets_planned > 0);
    if (!items.length) return;
    setWorkoutSession((prev) => {
      if (!prev) return prev;
      const currentLog = prev.setsLog || {};
      let changed = false;
      const newLog = { ...currentLog };
      for (const it of items) {
        if (!currentLog[it.id] || currentLog[it.id].length === 0) {
          const repsArr = (it.reps_planned || "").split(",").map((s) => s.trim()).filter(Boolean);
          newLog[it.id] = Array.from({ length: it.sets_planned }, (_, i) => ({
            set: i + 1,
            reps: repsArr[i] ?? repsArr[0] ?? null,
            weight: it.weight_planned || null,
            done: false,
            completedAt: null,
          }));
          changed = true;
        }
      }
      if (!changed) return prev;
      const updated = { ...prev, setsLog: newLog };
      try { localStorage.setItem(`workout_session_${updated.assignmentId}`, JSON.stringify(updated)); } catch (_) {}
      return updated;
    });
  }, [workoutSession?.id, selectedWorkoutAssignmentId, assignedWorkouts]); // eslint-disable-line react-hooks/exhaustive-deps


  const [openUsersModal, setOpenUsersModal] = useState(false);
  const [openWorkoutAdmin, setOpenWorkoutAdmin] = useState(false);
  const [openSelfWorkout, setOpenSelfWorkout] = useState(false);
  const [openMigrateWorkout, setOpenMigrateWorkout] = useState(false);
  const [openWorkoutCooldown, setOpenWorkoutCooldown] = useState(false);
  const [openStudentReport, setOpenStudentReport] = useState(false);
  const [rationaleModal, setRationaleModal] = useState({ open: false, text: null });
  const [myWorkoutRationale, setMyWorkoutRationale] = useState(null); // texto da explicação (ou null se não houver log de IA)
  const [openBodyMeasurements, setOpenBodyMeasurements] = useState(false);
  const [openNutrition, setOpenNutrition] = useState(false);
  const [openSelfInsights, setOpenSelfInsights] = useState(false);
  const [activeTopTab, setActiveTopTab] = useState(
    () => (user?.role === "professor" || user?.role === "admin") ? "teacher" : "overview"
  ); // 'overview' | 'teacher'

  // O useState acima só roda o inicializador NA MONTAGEM -- se o usuário virar
  // professor em runtime (self-service, GoogleWelcome.jsx -> handleBecomeProfessor
  // logo abaixo) o prop `user.role` muda via onUserUpdate, mas o Dashboard não
  // desmonta/remonta, então activeTopTab ficava travado em "overview" (tela
  // default de aluno) até um F5 forçar reinicialização do useState com o role
  // já atualizado. Efeito dedicado: só reage quando o role REALMENTE muda para
  // professor/admin vindo de outra coisa -- não interfere se o usuário trocar
  // de aba manualmente depois (isso não muda user?.role, então o efeito não
  // reexecuta). Ver docs/07-issues.md.
  const prevUserRoleRef = useRef(user?.role);
  useEffect(() => {
    const prevRole = prevUserRoleRef.current;
    const nextRole = user?.role;
    if (prevRole !== nextRole) {
      const becameProfessorOrAdmin =
        (nextRole === "professor" || nextRole === "admin") &&
        prevRole !== "professor" && prevRole !== "admin";
      if (becameProfessorOrAdmin) {
        setActiveTopTab("teacher");
      }
      prevUserRoleRef.current = nextRole;
    }
  }, [user?.role]);

  const [confirmDialog, setConfirmDialog] = useState(null);

  // Route local toast state to global provider
  useEffect(() => {
    if (!toast || !toast.message) return;
    const kind = String(toast.type || 'success').toLowerCase();
    if (kind === 'error') toastApi.error(toast.message);
    else if (kind === 'info') toastApi.info?.(toast.message);
    else toastApi.success(toast.message);
    setToast(null);
  }, [toast, toastApi]);

  function getUserMenuLabel(role) {
    if (role === "admin") {
      return {
        label: "Administrar usuários",
        sub: "Criar, editar e gerenciar usuários",
      };
    }
  
    if (role === "professor") {
      return {
        label: "Gerenciar alunos",
        sub: "Criar e acompanhar alunos",
      };
    }
  
    return {
      label: "Meu perfil",
      sub: "Atualizar foto e dados",
    };
  }

  const [form, setForm] = useState({
    title: "",
    description: "",
    goal_points: "",
    goal_type: "points",
    category_filter: "",
    min_duration_minutes: "",
    requires_location: false,
    location_lat: "",
    location_lon: "",
    location_radius_m: "",
    start_date: "",
    end_date: "",
    reward: "",
    weight: "",
    photo: null,
    is_public: false,
  });
  // Estado local só da UI do form de desafio (nao faz parte do payload) —
  // feedback de "capturando..." no botao de usar a localizacao atual como
  // ponto de referencia do geofence.
  const [capturingChallengeLocation, setCapturingChallengeLocation] = useState(false);

  const getErrorMessage = useCallback((err) => {
    const detail = err?.response?.data?.detail;
    if (!detail) return "Erro no check-in";
    if (Array.isArray(detail)) return detail.map((d) => d.msg).join(" • ");
    if (typeof detail === "string") return detail;
    return "Erro no check-in";
  }, []);

  const clamp = (n, min = 0, max = 100) => Math.max(min, Math.min(max, n));

  const toDateAtStart = (dateStr) => {
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Date(y, m - 1, d, 0, 0, 0, 0);
  };

  const toDateAtEndInclusive = (dateStr) => {
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Date(y, m - 1, d, 23, 59, 59, 999);
  };

  const calcDateProgress = (startStr, endStr) => {
    if (!startStr || !endStr) return 0;

    const start = toDateAtStart(startStr).getTime();
    const end = toDateAtEndInclusive(endStr).getTime();
    const now = Date.now();

    if (now <= start) return 0;
    if (now >= end) return 100;

    const pct = ((now - start) / (end - start)) * 100;
    return clamp(pct);
  };

  const daysBetweenInclusive = (startStr, endStr) => {
    const start = toDateAtStart(startStr);
    const end = toDateAtStart(endStr);
    const ms = end - start;
    const days = Math.floor(ms / (1000 * 60 * 60 * 24)) + 1;
    return Math.max(1, days);
  };

  const dayIndexInclusive = (startStr) => {
    const start = toDateAtStart(startStr);
    const today = toDateAtStart(todayLocalISO());
    const ms = today - start;
    return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)) + 1);
  };

  // Dias até o fim do desafio, inclusivo (termina hoje => 1 dia). 0 = encerrado.
  const daysRemaining = (endStr) => {
    if (!endStr) return 0;
    const end = toDateAtStart(endStr);
    const today = toDateAtStart(todayLocalISO());
    const ms = end - today;
    return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)) + 1);
  };

  // Dias até o INÍCIO do desafio (só faz sentido pra status "upcoming" —
  // challengeTemporalStatus já considera o próprio dia de start_date como
  // "active", então aqui start_date sempre está estritamente no futuro).
  // Sem +1 (diferente de daysRemaining): "amanhã" deve mostrar 1 dia, não 2.
  const daysUntilStart = (startStr) => {
    if (!startStr) return 0;
    const start = toDateAtStart(startStr);
    const today = toDateAtStart(todayLocalISO());
    const ms = start - today;
    return Math.max(0, Math.round(ms / (1000 * 60 * 60 * 24)));
  };

  const formatDateBR = (dateStr) => {
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
  };

  // "Em andamento" / "Em progresso" só fazem sentido dentro do período do
  // desafio — sem isso, um desafio com start_date futura já aparecia como
  // ativo desde o momento em que era criado (progresso sempre bate 0/meta
  // igual, então nada denunciava visualmente que ainda nem começou).
  const challengeTemporalStatus = (c) => {
    if (!c?.start_date || !c?.end_date) return "active";
    const now = Date.now();
    if (now < toDateAtStart(c.start_date).getTime()) return "upcoming";
    if (now > toDateAtEndInclusive(c.end_date).getTime()) return "ended";
    return "active";
  };

  const StatusBadge = ({ completed, challenge }) => {
    if (completed) return <span className={cn(styles.badge, styles.badgeSuccess)}>Concluído</span>;
    const temporal = challenge ? challengeTemporalStatus(challenge) : "active";
    if (temporal === "upcoming") return <span className={cn(styles.badge, styles.badgeWarn)}>Agendado</span>;
    if (temporal === "ended") return <span className={cn(styles.badge, styles.badgeNeutral)}>Encerrado</span>;
    return <span className={cn(styles.badge, styles.badgeNeutral)}>Em progresso</span>;
  };

  // Link público do desafio (Fase 6 — audiência) — exposto ao professor
  // quando o desafio é marcado como público, pra ele divulgar fora do app.
  const publicChallengeLink = (challengeId) => `${window.location.origin}/desafio/${challengeId}`;

  const copyPublicChallengeLink = (challengeId) => {
    const link = publicChallengeLink(challengeId);
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(link)
      .then(() => toastApi.success("Link do desafio copiado"))
      .catch(() => {});
  };

  // Ranking por período — busca inicial para o pill do header
  useEffect(() => {
    if (!user?.id) return;
    Promise.all([
      api.get("/exercises/ranking?period=weekly&limit=10"),
      api.get("/exercises/ranking?period=monthly&limit=10"),
    ])
      .then(([w, m]) => { setWeeklyRanking(w.data); setMonthlyRanking(m.data); })
      .catch(() => {});
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ranking por período (semanal / mensal) — refresh ao abrir modal
  useEffect(() => {
    if (!showRankingModal) return;
    setLoadingPeriodRanking(true);
    Promise.all([
      api.get("/exercises/ranking?period=weekly&limit=10"),
      api.get("/exercises/ranking?period=monthly&limit=10"),
    ])
      .then(([w, m]) => { setWeeklyRanking(w.data); setMonthlyRanking(m.data); })
      .catch(() => {})
      .finally(() => setLoadingPeriodRanking(false));
  }, [showRankingModal]);

  // Ranking por desafio
  useEffect(() => {
    if (!showRankingModal || rankingTab !== "challenge" || !rankingChallengeId) {
      setChallengeRanking([]);
      return;
    }
    setLoadingChallengeRanking(true);
    api.get(`/challenges/${rankingChallengeId}/ranking`)
      .then(res => setChallengeRanking(res.data))
      .catch(() => setChallengeRanking([]))
      .finally(() => setLoadingChallengeRanking(false));
  }, [showRankingModal, rankingTab, rankingChallengeId]);

  const userMenu = getUserMenuLabel(me?.role);
  const isManagerRole = me?.role === "admin" || me?.role === "professor";

  const openUserArea = useCallback(() => {
    if (isManagerRole) {
      setOpenUsersModal(true);
      return;
    }
    setShowProfileModal(true);
  }, [isManagerRole]);

  // ── Check-in (estado + lógica de submit) ─────────────────────────────────────
  const {
    showCheckinModal, setShowCheckinModal,
    showCardioModal, setShowCardioModal,
    checkingIn,
    checkinError, setCheckinError,
    selectedWorkoutAssignment,
    selectedCheckinTarget,
    confirmCheckin,
  } = useCheckin({
    user,
    setToast,
    exercises,
    assignedWorkouts,
    selectedWorkoutAssignmentId,
    setSelectedWorkoutAssignmentId,
    selectedChallenge,
    setSelectedChallenge,
    setSelectedExercise,
    form, setForm,
    workoutSession,
    allExercisesCompleted,
    elapsedSeconds,
    setTotalPoints,
    clearWorkoutSession,
    setWorkoutSummary,
    loadData,
    getErrorMessage,
    openConfirm,
  });
  // ─────────────────────────────────────────────────────────────────────────────

  // Preenche lat/lon do desafio com a localizacao atual do dispositivo —
  // fluxo pensado pro professor abrir o form em pe dentro da propria
  // academia na hora de criar o desafio, em vez de precisar descobrir e
  // digitar coordenadas manualmente (a UX mais provavel de erro que existe).
  const captureChallengeLocation = async () => {
    setCapturingChallengeLocation(true);
    try {
      const loc = await getCurrentPositionSafe();
      if (!loc) {
        setToast({ type: "error", message: "Não foi possível obter sua localização. Verifique a permissão do navegador." });
        return;
      }
      setForm((prev) => ({
        ...prev,
        location_lat: String(loc.lat),
        location_lon: String(loc.lon),
      }));
    } finally {
      setCapturingChallengeLocation(false);
    }
  };

  const createChallenge = async () => {
    if (!form.title || !form.description || !form.goal_points || !form.start_date || !form.end_date) {
      setToast({ type: "info", message: "Preencha todos os campos obrigatórios antes de salvar o desafio" });
      return;
    }

    try {
      const payload = {
        ...form,
        goal_points: Number(form.goal_points),
        goal_type: form.goal_type || "points",
        // "" no select vira ausência (Literal do backend não aceita string vazia)
        category_filter: form.category_filter || undefined,
        min_duration_minutes: form.min_duration_minutes ? Number(form.min_duration_minutes) : undefined,
        requires_location: !!form.requires_location,
        // Ponto de referencia do geofence (ex.: a academia do desafio) —
        // so faz sentido quando requires_location esta marcado; os 3 juntos
        // ou nenhum (ver _checkin_exclusion_reasons em user_service.py: se
        // faltar qualquer um, o backend cai no comportamento legado de "so
        // exige ter localizacao", sem comparar distancia nenhuma).
        location_lat: form.requires_location && form.location_lat !== "" ? Number(form.location_lat) : undefined,
        location_lon: form.requires_location && form.location_lon !== "" ? Number(form.location_lon) : undefined,
        location_radius_m: form.requires_location && form.location_radius_m !== "" ? Number(form.location_radius_m) : undefined,
      };
      if (selectedChallenge) {
        await api.put(`/users/${user.id}/challenges/${selectedChallenge.challenge_id}`, payload);
      } else {
        await api.post(`/users/${user.id}/challenges`, payload);
      }

      setToast({ type: "success", message: "Desafio salvo com sucesso!" });
      setShowCreateModal(false);
      setSelectedChallenge(null);
      setForm({
        title: "",
        description: "",
        goal_points: "",
        goal_type: "points",
        category_filter: "",
        min_duration_minutes: "",
        requires_location: false,
        location_lat: "",
        location_lon: "",
        location_radius_m: "",
        start_date: "",
        end_date: "",
        reward: "",
        is_public: false,
      });
      loadData();
    } catch (err) {
      setToast({ type: "error", message: getErrorMessage(err) });
    }
  };

  const joinChallenge = async (id) => {
    try {
      await api.post(`/challenges/${id}/participate`);
      setToast({ type: "success", message: "Participando do desafio!" });
      loadData();
    } catch (err) {
      setToast({ type: "error", message: err.response?.data?.detail || "Erro ao participar" });
    }
  };

  const sortWorkoutItems = (items = []) =>
    [...items].sort((a, b) => Number(a.order_index || 0) - Number(b.order_index || 0));

  const sanitizeName = (s) => (s || "").replace(/—|–/g, "-");
  const getWorkoutTitle = (assignment) =>
    sanitizeName(assignment?.custom_name || assignment?.workout_plan?.title || (assignment ? `Treino #${assignment.workout_plan_id}` : "Ficha de treino"));

  const getDayName = (idx) => {
    const names = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];
    const n = Number(idx);
    return Number.isFinite(n) && n >= 0 && n <= 6 ? names[n] : "";
  };

  // Aviso não-bloqueante de conflito de descanso (48h) — ver
  // app/api/user_workout_plans.py::_detect_rest_conflicts. O backend nunca
  // salva sozinho quando há conflito: devolve status "rest_conflict" e
  // espera confirm=true pra aplicar de fato.
  const patchWorkoutDay = async (assignmentId, dayOfWeek, confirmed = false) => {
    try {
      const res = await api.patch(`/user-workout-plans/${assignmentId}/day`, {
        day_of_week: dayOfWeek,
        confirm: confirmed,
      });
      if (res.data?.status === "rest_conflict") {
        setRestConflict({
          assignmentId,
          dayOfWeek,
          message: res.data.message,
          conflicts: res.data.conflicts || [],
        });
        return;
      }
      setAssignedWorkouts(prev =>
        prev.map(a => a.id === assignmentId ? { ...a, day_of_week: res.data.day_of_week } : a)
      );
      setToast({ message: "Dia atualizado!", type: "success" });
    } catch {
      setToast({ message: "Não foi possível atualizar o dia.", type: "error" });
    } finally {
      setEditingDayId(null);
    }
  };

  // Confirmar/cancelar o RestConflictModal (aviso não-bloqueante de 48h).
  const confirmRestConflict = async () => {
    if (!restConflict) return;
    setRestConflictSaving(true);
    try {
      await patchWorkoutDay(restConflict.assignmentId, restConflict.dayOfWeek, true);
    } finally {
      setRestConflictSaving(false);
      setRestConflict(null);
    }
  };

  const cancelRestConflict = () => setRestConflict(null);

  const sortAssignmentsBySuggestion = (list = []) => {
    const todayIdx = (new Date().getDay() + 6) % 7; // Mon=0..Sun=6
    return [...list].sort((a, b) => {
      const da = a?.day_of_week;
      const db = b?.day_of_week;
      const hasA = da !== null && da !== undefined;
      const hasB = db !== null && db !== undefined;
      if (hasA && hasB) {
        const aa = (Number(da) - todayIdx + 7) % 7;
        const bb = (Number(db) - todayIdx + 7) % 7;
        if (aa !== bb) return aa - bb;
      } else if (hasA !== hasB) {
        return hasA ? -1 : 1; // sugeridos primeiro
      }
      const ta = (a?.custom_name || a?.workout_plan?.title || `Treino #${a?.workout_plan_id || ''}`).toLowerCase();
      const tb = (b?.custom_name || b?.workout_plan?.title || `Treino #${b?.workout_plan_id || ''}`).toLowerCase();
      return ta.localeCompare(tb, 'pt-BR');
    });
  };

  // Treino em destaque na primeira dobra do mobile — achado P1 da auditoria
  // mobile de 06/09/2026: a ação principal do app (começar o treino de hoje)
  // ficava abaixo da barra de pontos e do painel de clima, exigindo scroll.
  // Prioridade: sessão em andamento > treino de hoje > próximo sugerido.
  const heroWorkout = useMemo(() => {
    const list = assignedWorkouts || [];
    if (!list.length) return null;
    const todayIdx = (new Date().getDay() + 6) % 7;

    const inProgress = workoutSession
      ? list.find((a) => String(a.id) === String(workoutSession.assignmentId))
      : null;
    if (inProgress) return { assignment: inProgress, kind: "inProgress" };

    const today = list.find((a) => Number(a.day_of_week) === todayIdx);
    if (today) return { assignment: today, kind: "today" };

    const next = sortAssignmentsBySuggestion(list)[0];
    return next ? { assignment: next, kind: "next" } : null;
  }, [assignedWorkouts, workoutSession]); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteChallenge = async (challengeId) => {
    try {
      await api.delete(`/challenges/${challengeId}`);
      setToast({ type: "success", message: "Desafio deletado!" });
      loadData();
    } catch (err) {
      setToast({ type: "error", message: getErrorMessage(err) });
    }
  };

  const openEditChallenge = (challenge) => {
    setForm({
      title: challenge.title,
      description: challenge.description,
      goal_points: challenge.goal_points,
      goal_type: challenge.goal_type || "points",
      category_filter: challenge.category_filter || "",
      min_duration_minutes: challenge.min_duration_minutes || "",
      requires_location: !!challenge.requires_location,
      location_lat: challenge.location_lat ?? "",
      location_lon: challenge.location_lon ?? "",
      location_radius_m: challenge.location_radius_m ?? "",
      start_date: challenge.start_date,
      end_date: challenge.end_date,
      reward: challenge.reward || "",
      is_public: !!challenge.is_public,
    });
    setSelectedChallenge(challenge);
    setShowCreateModal(true);
  };

  const calcProgress = (c) => Math.min(100, (c.progress_points / c.goal_points) * 100);

  // Tipos de meta do desafio (Fase 2). `unit` é o rótulo curto exibido no card;
  // `metaLabel` rotula o campo no form; `optLabel` aparece no seletor.
  const CHALLENGE_GOAL_TYPES = [
    { value: "points",      unit: "pts",    metaLabel: "Meta de pontos",       optLabel: "Pontos" },
    { value: "workouts",    unit: "treinos",metaLabel: "Meta de treinos",      optLabel: "Treinos (dias)" },
    { value: "minutes",     unit: "min",    metaLabel: "Meta de minutos",      optLabel: "Minutos" },
    { value: "streak_days", unit: "dias",   metaLabel: "Meta de dias seguidos",optLabel: "Dias seguidos (streak)" },
  ];
  const goalMeta = (gt) => CHALLENGE_GOAL_TYPES.find((t) => t.value === gt) || CHALLENGE_GOAL_TYPES[0];
  const goalUnit = (gt) => goalMeta(gt).unit;

  const myRankingUser = ranking.find((r) => r.id === user?.id);
  const myRankingPosition = myRankingUser ? `${myRankingUser.position}º` : "-";
  // Se está no top 10, mostrar "Top 10", se está fora mostrar posição real
  const isInTop10 = myRankingUser && myRankingUser.position <= 10;
  const topMicroLabel = isInTop10 ? "Top 10" : null;

  const myWeeklyRankEntry  = weeklyRanking.find(r => r.id === user?.id);
  const myMonthlyRankEntry = monthlyRanking.find(r => r.id === user?.id);
  const rankingPeriods = [
    { label: '7 dias',  pos: myWeeklyRankEntry  ? `${myWeeklyRankEntry.position}º`  : '-', tab: 'weekly'  },
    { label: '30 dias', pos: myMonthlyRankEntry ? `${myMonthlyRankEntry.position}º` : '-', tab: 'monthly' },
    { label: 'Geral',   pos: myRankingPosition,                                              tab: 'all'     },
  ];

  // ── Cancelar sessão ────────────────────────────────────────────────────
  const handleCancelSession = useCallback(async () => {
    if (!workoutSession?.id) return;
    try {
      await api.delete(`/workout-sessions/${workoutSession.id}`);
      clearWorkoutSession();
      setShowCancelSessionConfirm(false);
      setShowCheckinModal(false);
      setToast({ message: 'Treino cancelado.', type: 'info' });
    } catch (err) {
      setToast({ message: 'Erro ao cancelar treino.', type: 'error' });
    }
  }, [workoutSession, clearWorkoutSession, setToast, setShowCheckinModal]);

  // ── Finalizar antecipado ────────────────────────────────────────────────
  const handleFinishNow = useCallback(async () => {
    if (!workoutSession?.id) return;
    setFinishNowLoading(true);
    try {
      // Captura stats da sessão ANTES de limpar (setsLog pode ser perdido após clearWorkoutSession)
      const completions = workoutSession.exerciseCompletions || {};
      const setsLog     = workoutSession.setsLog || {};
      let totalSets = 0;
      let totalVolume = 0;
      const exerciseRows = [];
      const assignmentItems = (assignedWorkouts.find(a => String(a.id) === String(workoutSession?.assignmentId))?.items) || [];
      assignmentItems.forEach((item) => {
        const sid = String(item.id);
        const sets = (setsLog[sid] || []).filter((s) => s.done);
        // inclui exercícios com pelo menos 1 série feita OU marcados como concluídos
        if (!completions[sid] && sets.length === 0) return;
        totalSets += sets.length;
        let vol = 0;
        sets.forEach((s) => {
          if (s.weight) {
            const repsNum = parseFloat(String(s.reps || '0').match(/[\d.]+/)?.[0] || '0');
            if (repsNum > 0) vol += repsNum * s.weight;
          }
        });
        totalVolume += vol;
        exerciseRows.push({ name: item.exercise_item?.name || item.name || 'Exercício', sets: sets.length, volume: Math.round(vol) });
      });

      // Mesma captura não-bloqueante usada no check-in normal — sem isso, um
      // treino finalizado pelo botão "Finalizar" nunca gravava localização
      // (ver docs/07-issues.md).
      const location = await getCurrentPositionSafe();
      const res = await api.post(`/workout-sessions/${workoutSession.id}/finish-now`, location ? {
        checkin_lat: location.lat,
        checkin_lon: location.lon,
      } : undefined);
      setShowFinishNowConfirm(false);
      setShowCheckinModal(false);
      setWorkoutSummary({
        durationSecs: (res.data.total_duration_minutes || 0) * 60,
        totalExercises: exerciseRows.length || null,
        totalSets: totalSets || null,
        totalVolume: Math.round(totalVolume),
        exercises: exerciseRows,
        pointsAwarded: res.data.points_awarded || 0,
        volumeBonus: 0,
        personalRecords: res.data.personal_records || [],
        previousVolume: res.data.previous_volume ?? null,
        week: res.data.week ?? null,
      });
      clearWorkoutSession();
      loadData();
    } catch (err) {
      setToast({ message: 'Erro ao finalizar treino.', type: 'error' });
    } finally {
      setFinishNowLoading(false);
    }
  }, [workoutSession, assignedWorkouts, clearWorkoutSession, setWorkoutSummary, setToast, loadData, setShowCheckinModal]);

  const activeAssignmentForSession = assignedWorkouts.find(a => String(a.id) === String(workoutSession?.assignmentId));

  // Section refs for bottom-nav scroll
  const workoutSectionRef  = useRef(null);
  const evolutionSectionRef = useRef(null);

  // Seção visível — alimenta o estado ativo da barra inferior. Achado P1 da
  // auditoria mobile de 06/09/2026: a barra existia (e a classe
  // .bottomNavItemActive já estava no CSS), mas NENHUM item era marcado como
  // ativo — o usuário nunca sabia onde estava. Jakob's Law: quem vê uma
  // barra inferior espera que ela indique a seção atual.
  const [activeSection, setActiveSection] = useState("inicio");

  useEffect(() => {
    if (!isMobile) return;
    const targets = [
      { key: "treinos",  el: workoutSectionRef.current },
      { key: "evolucao", el: evolutionSectionRef.current },
    ].filter((t) => t.el);
    if (!targets.length) return;

    const visible = new Map();
    const decide = () => {
      // Topo da página vence qualquer seção: ali o usuário está no "Início".
      if (window.scrollY < 60) { setActiveSection("inicio"); return; }
      if (visible.get("evolucao")) { setActiveSection("evolucao"); return; }
      if (visible.get("treinos"))  { setActiveSection("treinos"); }
    };

    // Faixa central da viewport: a seção só conta como "atual" quando está
    // no meio da tela, não quando aparece só uma borda dela.
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        const key = targets.find((t) => t.el === e.target)?.key;
        if (key) visible.set(key, e.isIntersecting);
      });
      decide();
    }, { rootMargin: "-45% 0px -45% 0px", threshold: 0 });

    targets.forEach((t) => io.observe(t.el));
    window.addEventListener("scroll", decide, { passive: true });
    decide();

    return () => {
      io.disconnect();
      window.removeEventListener("scroll", decide);
    };
  }, [isMobile, loading, assignedWorkouts.length]);

  // Item da barra inferior que deve aparecer como ativo. Um modal aberto
  // ganha da seção visível: pro usuário, estar com a Nutrição aberta É estar
  // "em Nutrição", mesmo que por baixo a página siga na seção de treinos.
  const navActive =
      showMenu          ? "menu"
    : openNutrition     ? "nutricao"
    : showCardioModal   ? "cardio"
    : activeSection;

  // Auto-register when all exercises are completed (fires once per session)
  const autoRegisteredSessionRef = useRef(null);

  useEffect(() => {
    if (!workoutSession || !selectedWorkoutAssignment) return;
    if (!showCheckinModal) return;
    if (workoutModalReadonly) return;
    const items = selectedWorkoutAssignment.items || [];
    if (!items.length) return;
    const allDone = items.every((it) => !!workoutSession.exerciseCompletions?.[it.id]);
    if (!allDone) return;
    if (autoRegisteredSessionRef.current === workoutSession.id) return;
    autoRegisteredSessionRef.current = workoutSession.id;
    confirmCheckin();
  }, [workoutSession, selectedWorkoutAssignment, showCheckinModal, workoutModalReadonly]); // eslint-disable-line react-hooks/exhaustive-deps

  // Delay showing the summary modal so the last exercise completion feels earned
  const [delayedSummary, setDelayedSummary] = useState(null);
  useEffect(() => {
    if (!workoutSummary) { setDelayedSummary(null); return; }
    const endTime = Date.now() + 600;
    let t = setTimeout(() => setDelayedSummary(workoutSummary), 600);
    // PWA instalado em iOS suspende setTimeout quando a aba vai pra segundo
    // plano/tela bloqueia, sem disparar o callback — o resumo só aparecia
    // depois que o aluno saia e voltava ao app (relato real: iPhone 14, PWA
    // instalado). Mesmo padrao de recuperacao por visibilitychange ja usado
    // em RestTimer/RestBanner: se o prazo ja passou quando a aba volta a
    // ficar visivel, dispara na hora em vez de esperar um setTimeout que
    // pode nunca chegar a rodar.
    const onVisible = () => {
      if (!document.hidden && Date.now() >= endTime) {
        clearTimeout(t);
        setDelayedSummary(workoutSummary);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearTimeout(t);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [workoutSummary]);

  // Activity rings data
  const weeklyGoalN = Number(me?.weekly_goal || 3);
  const checkinsThisWeek = (() => {
    const now = new Date();
    // Start of current week: Monday at 00:00:00 local time
    const dayOfWeek = (now.getDay() + 6) % 7; // Mon=0 … Sun=6
    const monday = new Date(now);
    monday.setHours(0, 0, 0, 0);
    monday.setDate(now.getDate() - dayOfWeek);
    return checkins.filter(c => new Date(c.date) >= monday).length;
  })();
  const weeklyRingPct    = Math.min(100, (checkinsThisWeek / weeklyGoalN) * 100);

  const heroContent = (() => {
    const myWeekly = weeklyRanking.find(r => r.id === user?.id);
    const pos = myWeekly?.position;
    const myPts = myWeekly?.points ?? 0;
    const above = pos && pos > 1 ? weeklyRanking.find(r => r.position === pos - 1) : null;
    const gap = above ? above.points - myPts : null;
    const checkinHoje = Number(checkinsToday || 0);
    const statusHoje = checkinHoje > 0
      ? `${checkinHoje} treino${checkinHoje > 1 ? 's' : ''} hoje`
      : 'treino de hoje ainda não registrado';

    if (pos === 1) {
      return {
        eyebrow: 'Ranking semanal',
        titleNode: <><span style={{ color: 'var(--accent)' }}>1º lugar</span> no ranking semanal</>,
        desc: `${myPts} pts esta semana · ${statusHoje}`,
      };
    }
    if (pos && pos <= 3) {
      return {
        eyebrow: 'Ranking semanal',
        titleNode: <><span style={{ color: 'var(--accent)' }}>{pos}º lugar</span> no ranking semanal</>,
        desc: gap != null && gap > 0
          ? `Faltam ${gap} pts para ultrapassar o ${pos - 1}º lugar · ${statusHoje}`
          : `${myPts} pts esta semana · ${statusHoje}`,
      };
    }
    if (pos && pos <= 10) {
      return {
        eyebrow: 'Progresso da semana',
        titleNode: <><span style={{ color: '#f59e0b' }}>{checkinsThisWeek}</span> de {weeklyGoalN} check-ins esta semana</>,
        desc: gap != null && gap > 0
          ? `${pos}º no ranking semanal · faltam ${gap} pts para ultrapassar o ${pos - 1}º lugar`
          : `${pos}º lugar no ranking semanal · ${statusHoje}`,
      };
    }
    const remaining = Math.max(0, weeklyGoalN - checkinsThisWeek);
    const goalReached = checkinsThisWeek >= weeklyGoalN;
    return {
      eyebrow: 'Progresso da semana',
      titleNode: goalReached
        ? <><span style={{ color: 'var(--accent)' }}>Meta semanal atingida</span> · continue assim</>
        : <><span style={{ color: '#f59e0b' }}>{checkinsThisWeek}</span> de {weeklyGoalN} treinos esta semana</>,
      desc: goalReached
        ? `${checkinsThisWeek} treinos concluídos · ${statusHoje}`
        : `Registre mais ${remaining} treino${remaining !== 1 ? 's' : ''} para atingir a meta · ${statusHoje}`,
    };
  })();

  const dailyRingPct     = Math.min(100, (checkinsToday / 2) * 100);

  // Aplica cor de marca do professor sempre que me ou o tema mudarem
  // Deve ficar antes de qualquer early return para respeitar as Rules of Hooks
  useEffect(() => {
    applyProfessorTheme(me?.professor_brand_color ?? me?.own_brand_color ?? null, theme, {
      dark: me?.professor_bg_color_dark ?? me?.own_bg_color_dark ?? null,
      light: me?.professor_bg_color_light ?? me?.own_bg_color_light ?? null,
    });
  }, [
    me?.professor_brand_color, me?.own_brand_color,
    me?.professor_bg_color_dark, me?.own_bg_color_dark,
    me?.professor_bg_color_light, me?.own_bg_color_light,
    theme,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ícone do PWA — mesma lógica do App.js, mas a partir de `me` (pode estar
  // mais atualizado que `user` logo após o professor trocar o ícone).
  useEffect(() => {
    applyPwaManifest(
      me?.role, me?.id, me?.professor_id,
      me?.custom_app_icon_url, me?.professor_app_icon_url,
      apiUrl,
      me?.name, me?.professor_name
    );
  }, [me?.role, me?.id, me?.professor_id, me?.professor_app_icon_url, me?.custom_app_icon_url, me?.name, me?.professor_name]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cycling automático do pill de ranking
  useEffect(() => {
    const t = setInterval(() => setRankingPillIdx(i => (i + 1) % 3), 3000);
    return () => clearInterval(t);
  }, []);

  // Explicação do treino (aluno): só mostra o item de menu se houver log de IA vinculado ao plano atual
  useEffect(() => {
    if (me?.role !== "aluno" || !me?.id) { setMyWorkoutRationale(null); return; }
    let cancelled = false;
    api.get("/ai/my-workout-rationale")
      .then(res => { if (!cancelled) setMyWorkoutRationale(res.data.rationale || null); })
      .catch(() => { if (!cancelled) setMyWorkoutRationale(null); });
    return () => { cancelled = true; };
  }, [me?.role, me?.id]);

  // Lógica de cooldown para geração de treino por self-service (consulta ai_usage_logs)
  const [workoutCooldownInfo, setWorkoutCooldownInfo] = useState(null);

  // Aviso de renovação — aluno self-service com treino gerado há mais de 90 dias
  // (badge pulsante no header, ver R157 em docs/07-issues.md). Fetch proativo,
  // diferente do handleAiWorkoutClick (que só consulta ao clicar em "Gerar treino").
  const [renewalInfo, setRenewalInfo] = useState(null);
  const [openRenewalReminder, setOpenRenewalReminder] = useState(false);
  useEffect(() => {
    if (me?.role !== "aluno" || !me?.is_self_service) { setRenewalInfo(null); return; }
    let cancelled = false;
    api.get("/ai/self-workout/last-usage")
      .then(res => { if (!cancelled) setRenewalInfo(res.data); })
      .catch(() => { if (!cancelled) setRenewalInfo(null); });
    return () => { cancelled = true; };
  }, [me?.role, me?.is_self_service, me?.id]);
  const showRenewalBadge = !!(renewalInfo?.last_generated_at && renewalInfo?.can_generate);

  // Aviso de instalação do PWA — aluno acessando pelo navegador, sem o app na
  // tela inicial. É pré-requisito real pra notificação push (o Safari do
  // iPhone só entrega push pra site instalado), então o badge fica fixo até o
  // aluno instalar, em vez de dispensável. Ver hooks/usePwaInstall.js.
  const pwa = usePwaInstall();
  const [openInstallPwa, setOpenInstallPwa] = useState(false);

  // Apelo de instalação ao FIM de um treino (prescrito ou cardio), ainda no
  // navegador. É o momento de maior "prova de valor" — o aluno acabou de
  // registrar algo — então é quando o convite converte melhor que o badge do
  // sino sozinho. Teto de 1x/dia via localStorage pra ser apelo, não spam.
  const cardioSavedRef = useRef(false);
  // Mesmo reforço de loadData() que useCheckin.js/confirmCheckin já fazia pro
  // checkin de treino prescrito — o cardio avulso (CardioCheckinModal) nunca
  // tinha esse retry, só o loadData() único do onSuccess abaixo, então o
  // rótulo de localização (resolvido em background no servidor) nunca
  // aparecia sozinho nesse fluxo. Ver R238 em docs/07-issues.md.
  const cardioLabelTimersRef = useRef([]);
  useEffect(() => {
    return () => {
      cardioLabelTimersRef.current.forEach(clearTimeout);
      cardioLabelTimersRef.current = [];
    };
  }, []);
  const maybeNudgeInstallAfterWorkout = useCallback(() => {
    try {
      if (!pwa.shouldNudgeInstall) return;   // já instalado, ou plataforma sem push
      const KEY = "installNudgeAfterWorkoutAt";
      const last = Number(localStorage.getItem(KEY) || 0);
      if (Date.now() - last < 24 * 60 * 60 * 1000) return;  // no máximo 1x/dia
      localStorage.setItem(KEY, String(Date.now()));
      setOpenInstallPwa(true);
    } catch (_) { /* localStorage indisponível — ignora */ }
  }, [pwa.shouldNudgeInstall]);
  // Aluno E professor: o professor é destinatário de 2 dos 5 gatilhos
  // (dificuldade relatada e treino concluído), então precisa do app instalado
  // tanto quanto o aluno. Antes o convite era só pra aluno — herança de quando
  // as notificações ainda não existiam.
  const canReceivePush = me?.role === "aluno" || me?.role === "professor";
  const showInstallPwaBadge = !!(canReceivePush && pwa.shouldNudgeInstall);

  // Ativação das notificações, DEPOIS do app instalado.
  //
  // Não dá pra chamar Notification.requestPermission() sozinho no carregamento:
  // o WebKit (Safari/iOS) exige que o pedido de permissão parta de um gesto do
  // usuário, então um pedido automático no mount é descartado sem nunca exibir
  // o diálogo — o aluno instalaria o app e jamais receberia notificação. Por
  // isso a permissão é pedida no clique do badge do sino.
  //
  // Quando a permissão JÁ foi concedida (aluno reinstalou, trocou de conta,
  // limpou o app), aí sim reassina em silêncio: não há diálogo a exibir, é só
  // registrar o endpoint atual no backend.
  const [pushPermission, setPushPermission] = useState(
    () => (typeof Notification !== "undefined" ? Notification.permission : "unsupported")
  );
  const pushSyncedRef = useRef(false);
  useEffect(() => {
    if (!me?.id || !pwa.isStandalone || !pwa.pushSupported) return;
    if (pushSyncedRef.current || pushPermission !== "granted") return;
    pushSyncedRef.current = true;
    subscribeToPush().catch(() => {});
  }, [me?.id, pwa.isStandalone, pwa.pushSupported, pushPermission]);

  // Badge do sino: só quando o app está instalado, o push é suportado e a
  // permissão ainda não foi decidida. Se o aluno negar, some (o navegador não
  // reabre o diálogo mesmo, insistir só geraria frustração).
  const showEnablePushBadge = !!(
    canReceivePush && pwa.isStandalone && pwa.pushSupported && pushPermission === "default"
  );

  const handleEnablePush = async () => {
    // Marca como sincronizado ANTES de atualizar a permissão: senão o effect
    // acima dispararia um segundo subscribeToPush assim que pushPermission
    // virasse "granted" (inofensivo, mas é uma chamada redundante à API).
    pushSyncedRef.current = true;
    const result = await subscribeToPush();
    const permission = typeof Notification !== "undefined" ? Notification.permission : "unsupported";
    setPushPermission(permission);
    // Fim do funil de notificação: separa quem aceitou de quem negou e de quem
    // nunca viu o pedido (push_prompt_shown sem push_granted/push_denied).
    logPwaEvent(
      result?.ok ? "push_granted"
        : permission === "denied" ? "push_denied"
        // "default": fechou o dialogo do sistema sem escolher - nao e erro.
        : permission === "default" ? "push_ignored"
        : "push_failed",
      { platform: pwa.platform, browser: pwa.browser },
    );

    if (result?.ok) {
      toastApi.success("Notificações ativadas!");
    } else if (permission === "denied") {
      toastApi.info("Notificações bloqueadas. Para liberar, ajuste as permissões do app no aparelho.");
    } else if (result?.reason === "server-disabled") {
      toastApi.error("As notificações ainda não estão disponíveis. Tente mais tarde.");
    }
  };

  // Convite grande para ativar as notificações: o sino do header sozinho passava
  // despercebido e boa parte de quem instalava o app nunca ativava os avisos.
  // Aparece no máximo 3 vezes, com 3 dias entre elas, e some sozinho quando a
  // permissão deixa de ser "default" (aceitou ou negou). A contagem é por
  // usuário (mesmo aparelho pode ter mais de uma conta) e só avança quando o
  // convite de fato aparece: aba em segundo plano ou treino em andamento não
  // gastam uma das 3 chances.
  const [openEnablePush, setOpenEnablePush] = useState(false);
  const pushPromptShownRef = useRef(false);
  const hasWorkoutSession = !!workoutSession;
  useEffect(() => {
    if (!showEnablePushBadge || !me?.id || hasWorkoutSession || pushPromptShownRef.current) return undefined;
    const key = `enablePushPromptState:${me.id}`;
    const MAX_SHOWS = 3;
    const GAP_MS = 3 * 24 * 60 * 60 * 1000;
    let state = { count: 0, last: 0 };
    try {
      state = { ...state, ...JSON.parse(localStorage.getItem(key) || "{}") };
    } catch (_) { /* storage indisponível ou corrompido - trata como primeira vez */ }
    if (state.count >= MAX_SHOWS || Date.now() - state.last < GAP_MS) return undefined;

    // Já deixa a chave pública em memória: no toque, o pedido de permissão sai
    // sem esperar rede (o Safari exige que saia do gesto).
    prefetchPushPublicKey().catch(() => {});

    // Pequeno atraso: deixa o painel carregar antes de cobrir a tela. A marca
    // de "já mostrei" só vale quando o convite aparece de verdade; se o efeito
    // for refeito antes (cleanup limpa o timer), ele agenda de novo.
    const timer = setTimeout(() => {
      if (document.visibilityState !== "visible") return;
      pushPromptShownRef.current = true;
      try {
        localStorage.setItem(key, JSON.stringify({ count: state.count + 1, last: Date.now() }));
      } catch (_) { /* sem storage: o convite aparece, só não conta */ }
      logPwaEvent("push_prompt_shown", { platform: pwa.platform, browser: pwa.browser });
      setOpenEnablePush(true);
    }, 1500);
    return () => clearTimeout(timer);
  }, [showEnablePushBadge, me?.id, hasWorkoutSession, pwa.platform, pwa.browser]);

  // Fecha só depois do pedido terminar: o botão mostra "Ativando..." enquanto
  // o dialogo do sistema está aberto.
  const handleEnablePushFromPrompt = async () => {
    try {
      await handleEnablePush();
    } finally {
      setOpenEnablePush(false);
    }
  };
  const handleDismissPushPrompt = () => {
    setOpenEnablePush(false);
    logPwaEvent("push_dismissed", { platform: pwa.platform, browser: pwa.browser });
  };

  // useCallback porque este handler entra no menuOptions: recriado a cada
  // render, ele invalidaria o useMemo sempre, tornando a memoizacao inutil.
  const handleAiWorkoutClick = useCallback(async () => {
    if (!me?.is_self_service) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${apiUrl}/ai/self-workout/last-usage`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      // Prioridade: limite de renovações antes de cooldown
      if (data.has_reached_renovation_limit) {
        setOpenSelfWorkout(true);
      } else if (!data.can_generate) {
        setWorkoutCooldownInfo(data);
        setOpenWorkoutCooldown(true);
      } else {
        setOpenSelfWorkout(true);
      }
    } catch {
      // Em caso de erro de rede, deixa abrir o modal normalmente
      setOpenSelfWorkout(true);
    }
  }, [me?.is_self_service, apiUrl]);

  // ── Self-service: virar professor a partir da tela de boas-vindas do Google
  // (GoogleWelcome.jsx) — substitui o antigo "Enviar email" pedindo pra
  // administração criar a conta manualmente. Ver POST /users/me/become-professor
  // (app/api/users/profile.py).
  const [becomingProfessor, setBecomingProfessor] = useState(false);

  const handleBecomeProfessor = useCallback(() => {
    if (becomingProfessor) return;
    openConfirm({
      title: "Virar professor?",
      message: "Sua conta passa a ter acesso de professor: gestão de alunos, criação de fichas com IA e acompanhamento de evolução em tempo real. Essa troca é definitiva: você não vai conseguir voltar a ser aluno sozinho depois -- será necessário entrar em contato com o suporte para reverter.",
      icon: faChalkboardUser,
      confirmLabel: "Quero ser professor",
      confirmIcon: faChalkboardUser,
      confirmVariant: "success",
      onConfirm: async () => {
        setBecomingProfessor(true);
        try {
          const { data } = await api.post("/users/me/become-professor");
          setMe(data);
          setTotalPoints(data.points);
          onUserUpdate?.(data);
          toastApi.success("Pronto! Sua conta agora é de professor.");
        } catch (err) {
          const msg = err?.response?.data?.detail?.message || "Não foi possível concluir agora. Tente de novo em instantes.";
          toastApi.error(msg);
        } finally {
          setBecomingProfessor(false);
        }
      },
    });
  }, [
    becomingProfessor, openConfirm, onUserUpdate, toastApi,
    // setMe/setTotalPoints vem de hook customizado (useDashboardData) por
    // destructuring — mesmo caso de setShowCardioModal logo abaixo, ESLint
    // não reconhece como setter estável de useState nativo.
    setMe, setTotalPoints,
  ]);

  // ── Menu de acoes: FONTE UNICA ────────────────────────────────────────────
  // Antes existiam DUAS listas independentes: este array (desktop, via
  // <ActionMenu>) e uma folha escrita a mao com ~19 <button> fixos no JSX do
  // mobile. Alem do custo de manter as duas em sincronia, elas ja divergiam:
  // o sheet mobile nunca replicou o gate `hasProfessorBillingAccess`, entao
  // um professor SEM plano ativo enxergava "Gerenciar treinos", "Nutricao" e
  // "Editar perfil" no celular — itens que o desktop escondia. Ver
  // docs/12-auditoria-mobile-professor.md (achado M4).
  //
  // Os itens de admin "Consumo de IA" e "Enviar email" existiam so no mobile
  // (no desktop moram nos atalhos do AdminDashboard); foram mantidos aqui,
  // entao agora aparecem nos dois — aditivo, ninguem perde atalho.
  const menuOptions = useMemo(() => [
                ...(me?.role === "aluno" && !me?.organization_id && (!me?.professor_id || me?.professor_has_billing_plan)
                  ? [{ key: "payments", icon: faCreditCard, label: "Pagamentos", sub: "Gerenciar assinatura ou pagar avulso", onClick: () => setShowPaymentsModal(true) }]
                  : []),
                ...(me?.role === "professor" && !me?.organization_id && (me?.is_self_service === true || me?.student_billing_override === true)
                  ? [{ key: "professorBilling", icon: faUserGroup, label: "Gestão de pagamentos", sub: "Seu pagamento e o dos seus alunos", onClick: () => setShowProfessorBilling(true) }]
                  : []),
                ...(me?.role === "admin"
                  ? [{ key: "profile", icon: faPenToSquare, label: userMenu.label, sub: userMenu.sub, onClick: openUserArea }]
                  : []),
                ...(me?.role === "aluno"
                  ? [{ key: "profile", icon: faPenToSquare, label: userMenu.label, sub: userMenu.sub, onClick: openUserArea }]
                  : []),
                ...(me?.role === "admin"
                  ? [{ key: "aiUsage", icon: faRobot, label: "Consumo de IA", sub: "Gerações, custo e histórico", onClick: () => setShowAiUsageModal(true) }]
                  : []),
                ...(me?.role === "admin"
                  ? [{ key: "sendEmail", icon: faEnvelopeOpenText, label: "Enviar email", sub: "Comunicado em massa", onClick: () => setShowSendEmailModal(true) }]
                  : []),
                ...(me?.role === "admin"
                  ? [{ key: "sendPush", icon: faBell, label: "Enviar notificação", sub: "Em nome do TreinoNexo", onClick: () => setShowAdminPushModal(true) }]
                  : []),
                ...(me?.role === "professor" || me?.role === "admin"
                  ? [{ key: "create", icon: faFireFlameCurved, label: "Criar desafio", sub: "Novo desafio para a galera", onClick: () => setShowCreateModal(true) }]
                  : []),
                { key: "challenges", icon: faMedal, label: "Desafios disponíveis", sub: "Ver andamento e metas", onClick: () => setShowChallengesModal(true) },
                ...(me?.role === "aluno" && myWorkoutRationale
                  ? [{ key: "workoutRationale", icon: faCircleInfo, label: "Explicação do treino", sub: "Entenda como seu treino foi montado",
                       onClick: () => setRationaleModal({ open: true, text: myWorkoutRationale }) }]
                  : []),
                  ...(me?.role === "aluno" && me?.is_self_service
                  ? [{ key: "aiWorkout", icon: faWandMagicSparkles, label: "Gerar meu treino", sub: "Montar ou renovar minha ficha", onClick: handleAiWorkoutClick }]
                  : []),
                  ...(me?.role === "aluno" && me?.is_self_service && hasPaidAccess
                  ? [{ key: "selfInsights", icon: faLightbulb, label: "Análise da minha evolução", sub: "Aderência, volume e progresso do seu treino", onClick: () => setOpenSelfInsights(true), plus: true }]
                  : []),
                ...(me?.role === "professor"
                  ? [{ key: "profile", icon: faPenToSquare, label: userMenu.label, sub: userMenu.sub, onClick: openUserArea, hidden: !hasProfessorBillingAccess }]
                  : []),
                // Academia (multi-professor): dono/gestor administram; professor
                // comum só consulta a lista de professores. Ver OrganizationModal.
                ...(me?.role === "professor" && me?.organization_id && me?.org_role
                  ? [{ key: "organization", icon: faBuilding, label: "Minha academia",
                       sub: me.org_role === "owner" || me.org_role === "manager" ? "Professores e alunos da academia" : "Professores da academia",
                       onClick: () => setShowOrganization(true) }]
                  : []),
                // Dono paga o plano da academia (preço definido pelo admin);
                // some enquanto a academia estiver em cortesia.
                ...(me?.role === "professor" && me?.org_role === "owner" && me?.org_billing && me.org_billing.status !== "cortesia"
                  ? [{ key: "orgPayments", icon: faCreditCard, label: "Assinatura da academia", sub: "Plano e pagamento da academia", onClick: () => setShowPaymentsModal(true) }]
                  : []),
                ...(me?.role === "professor" || me?.role === "admin"
                  ? [{ key: "adminWork", icon: faFireFlameCurved, label: "Gerenciar treinos", sub: "Criar treinos, relacionar alunos...", onClick: () => setOpenWorkoutAdmin(true), hidden: me?.role === "professor" && !hasProfessorBillingAccess, plus: me?.role === "professor" && me?.is_self_service === true }]
                  : []),
                ...(me?.role === "aluno"
                  ? [{ key: "bodyMeasurements", icon: faDumbbell, label: "Medidas corporais", sub: "Gordura, TMB e mais", onClick: () => setOpenBodyMeasurements(true), hidden: me?.is_self_service && !hasPaidAccess, plus: !!me?.is_self_service }]
                  : []),
                ...(me?.role === "professor" || me?.role === "admin"
                  ? [{ key: "professorBodyMeasurements", icon: faDumbbell, label: "Medidas corporais", sub: "Gerenciar medidas dos alunos", onClick: () => setOpenBodyMeasurements(true), hidden: me?.role === "professor" && !hasProfessorBillingAccess, plus: me?.role === "professor" && me?.is_self_service === true }]
                  : []),
                ...(me?.role === "aluno"
                  ? [{ key: "nutrition", icon: faUtensils, label: "Nutrição", sub: "Dieta, macros e alimentos", onClick: () => setOpenNutrition(true), hidden: me?.is_self_service && !hasPaidAccess, plus: !!me?.is_self_service }]
                  : []),
                ...(me?.role === "professor" || me?.role === "admin"
                  ? [{ key: "professorNutrition", icon: faUtensils, label: "Nutrição", sub: "Gerenciar dietas dos alunos", onClick: () => setOpenNutrition(true), hidden: me?.role === "professor" && !hasProfessorBillingAccess, plus: me?.role === "professor" && me?.is_self_service === true }]
                  : []),
                { key: "cardio", icon: faArrowTrendUp, label: "Registrar cardio", sub: "Esteira, corrida, spinning...", onClick: () => setShowCardioModal(true), hidden: me?.is_self_service },
                ...(me?.role === "aluno"
                  ? [{ key: "studentReport", icon: faComments, label: me?.professor_id ? "Falar com professor" : "Precisa de ajuda?", sub: me?.professor_id ? "Fale direto com seu professor" : "Fale com nossa equipe", onClick: () => setOpenStudentReport(true) }]
                  : []),
                { key: "logout", icon: faRightFromBracket, label: "Sair",
                  sub: `Sessão expira em ${minutes}:${String(seconds).padStart(2, "0")}`,
                  variant: "danger",
                  onClick: () => openConfirm({ title: "Sair da conta?", message: "Deseja encerrar sua sessão?", confirmLabel: "Sair", confirmIcon: faRightFromBracket, confirmVariant: "danger", onConfirm: () => onLogout(true) }) },
              ], [
    me, userMenu, minutes, seconds, myWorkoutRationale,
    hasPaidAccess, hasProfessorBillingAccess,
    handleAiWorkoutClick, openUserArea, openConfirm, onLogout,
    // Vem do useCheckin por destructuring, entao o ESLint nao o reconhece
    // como setter estavel de useState — precisa estar declarado aqui.
    setShowCardioModal,
  ]);

  if (!user?.id) return <p className={styles.loading}>Carregando…</p>;

  if (loading) return <DashboardSkeleton isMobile={isMobile} />;

  if (user?.role === "google") {
    return (
      <>
        <GoogleWelcome
          user={me || user}
          apiUrl={apiUrl}
          onLogout={() => onLogout(false)}
          onOpenProfile={() => setOpenUsersModal(true)}
          onAiWorkout={() => setOpenSelfWorkout(true)}
          onMigrateWorkout={() => setOpenMigrateWorkout(true)}
          onOpenSupport={() => setOpenStudentReport(true)}
          onBecomeProfessor={handleBecomeProfessor}
          becomingProfessor={becomingProfessor}
        />
        {openUsersModal && (
          <UserAdminModal
            open={openUsersModal}
            onClose={() => setOpenUsersModal(false)}
            apiBase={apiUrl}
            token={localStorage.getItem("token")}
            onUpdated={(nextMe) => {
              setMe(nextMe);
              setTotalPoints(nextMe.points);
              onUserUpdate?.(nextMe);
            }}
          />
        )}
        <SelfWorkoutModal
          open={openSelfWorkout}
          onClose={() => {
            setOpenSelfWorkout(false);
            // A geração persiste + vira aluno no backend já no preview. Se o aluno
            // fecha sem confirmar, revalida aqui pra não voltar pro GoogleWelcome
            // com o role velho em memória. Ver docs/07-issues.md.
            api.get("/users/me").then((r) => {
              if (r?.data?.id) { onUserUpdate?.(r.data); loadData(); }
            }).catch(() => {});
          }}
          onSuccess={(freshProfile) => { onUserUpdate?.(freshProfile ?? { role: "aluno" }); loadData(); }}
          onOpenSupport={() => { setOpenSelfWorkout(false); setTimeout(() => setOpenStudentReport(true), 200); }}
          onOpenPayments={() => { setOpenSelfWorkout(false); setTimeout(() => setShowPaymentsModal(true), 200); }}
          user={me}
        />
        <MigrateWorkoutModal
          open={openMigrateWorkout}
          onClose={() => {
            setOpenMigrateWorkout(false);
            // Mesma razão do SelfWorkoutModal: a prévia já persiste + vira aluno
            // no backend. Revalida aqui pra não voltar pro GoogleWelcome com o
            // role velho em memória se o aluno fechar sem confirmar.
            api.get("/users/me").then((r) => {
              if (r?.data?.id) { onUserUpdate?.(r.data); loadData(); }
            }).catch(() => {});
          }}
          onSuccess={(freshProfile) => { onUserUpdate?.(freshProfile ?? { role: "aluno" }); loadData(); }}
          onOpenSupport={() => { setOpenMigrateWorkout(false); setTimeout(() => setOpenStudentReport(true), 200); }}
          onOpenPayments={() => { setOpenMigrateWorkout(false); setTimeout(() => setShowPaymentsModal(true), 200); }}
          onSwitchToBuilder={() => { setOpenMigrateWorkout(false); setTimeout(() => setOpenSelfWorkout(true), 200); }}
          user={me}
        />
        {openStudentReport && (
          <StudentReportModal
            isOnboarding={true}
            onClose={() => setOpenStudentReport(false)}
          />
        )}
        {/* Este branch (role=google) tem return próprio, fora da árvore do
            Dashboard "normal" lá embaixo — sem isto aqui, openConfirm()
            (usado por handleBecomeProfessor) atualizava o estado mas não
            existia nenhum <ConfirmDialog> montado pra mostrar a caixa. */}
        <ConfirmDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          cancelLabel={confirmState.cancelLabel}
          confirmIcon={confirmState.confirmIcon}
          cancelIcon={confirmState.cancelIcon}
          icon={confirmState.icon}
          confirmVariant={confirmState.confirmVariant}
          onDismiss={closeConfirm}
          onClose={closeConfirm}
          onConfirm={runConfirm}
        />
      </>
    );
  }

  const allChallengesUnified = allChallenges.map((c) => {
    const challengeId = getChallengeId(c);
    const myChallenge = myChallenges.find((mc) => getChallengeId(mc) === challengeId);
    const progress = myChallenge?.progress_points ?? 0;

    return {
      ...c,
      challenge_id: challengeId,
      isCreator: c.creator_id === user.id,
      isParticipant: !!myChallenge,
      progress_points: progress,
      completed: progress >= c.goal_points,
    };
  });

  // Lista de desafios do aluno: encerrado há mais de 7 dias sai da lista (já deu
  // tempo de ver resultado, ranking e recompensa). Só afeta a lista: ranking e
  // estatísticas continuam usando allChallengesUnified inteiro. Professor e
  // admin seguem vendo todos os encerrados (gestão/histórico).
  const CHALLENGE_ENDED_VISIBLE_DAYS = 7;
  const visibleChallenges = me?.role === "aluno"
    ? allChallengesUnified.filter((c) => {
        if (!c.end_date) return true;
        const endedMs = Date.now() - toDateAtEndInclusive(c.end_date).getTime();
        return !(endedMs > CHALLENGE_ENDED_VISIBLE_DAYS * 24 * 60 * 60 * 1000);
      })
    : allChallengesUnified;

  // antigo getRemainingCheckins removido — regra agora é global (2 por dia)

  // R218: "isParticipant" sozinho não bastava — incluía desafios em que o
  // aluno está inscrito mas que AINDA NÃO COMEÇARAM ("upcoming"). Isso fazia
  // getActiveCheckinChallenge() escolher um desafio futuro como alvo do
  // check-in, e o backend rejeitava o registro inteiro (treino incluído) com
  // "Data retroativa não pode ser menor que a data de início do desafio" —
  // mesmo o aluno tendo acabado de treinar agora, sem nada de retroativo.
  // Excluir "upcoming" aqui corrige a seleção de alvo (cai pro check-in
  // avulso, sem desafio, quando só há desafios futuros) e também deixa
  // avgChallengePct/completedChallenges mais corretos (desafio que não
  // começou não deveria contar como "ativo" em nenhuma dessas contas).
  const activeParticipantChallenges = allChallengesUnified.filter(
    (c) => c.isParticipant && challengeTemporalStatus(c) !== "upcoming"
  );
  // Regra estrita de photo_required (ver docs/07-issues.md): se QUALQUER
  // desafio ativo em andamento exige foto, o check-in precisa vir com foto
  // — não só o desafio escolhido na tela. O backend é quem de fato aplica
  // (422 se faltar); isto aqui é só o aviso antecipado na UI.
  // Desafio encerrado não exige nem conta mais nada (nem foto): só os que
  // estão dentro do período valem para o check-in, completos ou não.
  const checkinChallenges = activeParticipantChallenges.filter(
    (c) => challengeTemporalStatus(c) === "active"
  );
  const photoRequiredNow = checkinChallenges.some((c) => c.photo_required && !c.completed);
  const avgChallengePct = activeParticipantChallenges.length
    ? activeParticipantChallenges.reduce((s, c) => s + calcProgress(c), 0) / activeParticipantChallenges.length
    : 0;
  const completedChallenges = activeParticipantChallenges.filter((c) => c.completed).length;
  // Regra global: máximo de 2 registros por dia (independente do desafio/ficha)
  const availableCheckinsToday = Math.max(0, 2 - Number(checkinsToday || 0));

  const nextCheckinChallenge = checkinChallenges.find((challenge) => availableCheckinsToday > 0 && !challenge.completed);

  const getActiveCheckinChallenge = () => {
    if (availableCheckinsToday <= 0) return null;
    const candidate =
      nextCheckinChallenge ||
      checkinChallenges.find((c) => !c.completed) ||
      checkinChallenges[0] ||
      null;
    return candidate ? { ...candidate, challenge_id: getChallengeId(candidate) } : null;
  };


  const openWorkoutCta = async (assignment) => {
    if (!assignment) {
      setToast({ type: "info", message: "Voce ainda nao tem treinos ativos vinculados." });
      return;
    }

    // Enforce daily limit: if 0 remaining, open read-only and warn
    if (availableCheckinsToday <= 0) {
      setSelectedExercise(null);
      setSelectedWorkoutAssignmentId(String(assignment.id));
      setWorkoutModalReadonly(true);
      setWorkoutModalPreStart(false);
      setShowCheckinModal(true);
      setSelectedChallenge(getActiveCheckinChallenge());
      setToast({ type: 'info', message: 'Limite diário de 2 registros atingido.' });
      return;
    }

    // Ensure we have the latest active session from server (handles F5/logoff/login cases).
    // Merge with current local state so in-progress sets are preserved.
    let active = workoutSession;
    try {
      const act = await api.get('/workout-sessions/active');
      if (act?.data?.active) {
        const merged = parseActiveSession(act.data, workoutSession);
        setWorkoutSession(merged);
        active = merged;
      }
    } catch (_) {}

    // If there's an active session (fresh from state or server)
    if (active) {
      if (active.assignmentId === assignment.id) {
        // resume current session (editable)
        setSelectedExercise(null);
        setSelectedWorkoutAssignmentId(String(assignment.id));
        setWorkoutModalPreStart(false);
        setWorkoutModalReadonly(false);
        setShowCheckinModal(true);
        setSelectedChallenge((prev) => prev || getActiveCheckinChallenge());
        return;
      }

      // different assignment: open readonly view
      setSelectedExercise(null);
      setSelectedWorkoutAssignmentId(String(assignment.id));
      setWorkoutModalReadonly(true);
      setWorkoutModalPreStart(false);
      setShowCheckinModal(true);
      setSelectedChallenge((prev) => prev || getActiveCheckinChallenge());
      setToast({ type: 'info', message: 'Você tem um treino em andamento — visualização somente leitura.' });
      return;
    }

    // No active session: use custom ConfirmDialog to ask start or view-only.
    setSelectedExercise(null);
    setSelectedWorkoutAssignmentId(String(assignment.id));
    setPendingViewAssignmentId(String(assignment.id));
    setSelectedChallenge(getActiveCheckinChallenge());
    openConfirm({
      title: "O que deseja fazer?",
      message: (
        <div style={{ textAlign: "left", fontSize: 14, lineHeight: 1.5, color: "var(--text-0-60)" }}>
          <p style={{ margin: "0 0 10px" }}>
            <strong style={{ color: "var(--blue)" }}>Iniciar treino</strong> - já está na academia e vai
            treinar agora? Registra a sessão: abre para preenchimento de repetições e pesos.
          </p>
          <p style={{ margin: 0 }}>
            <strong style={{ color: "var(--text-primary)" }}>Só visualizar</strong> - abre a lista de
            exercícios sem registrar nada.
          </p>
        </div>
      ),
      icon: faDumbbell,
      confirmLabel: "Iniciar treino",
      confirmIcon: faPlay,
      confirmVariant: "success",
      cancelLabel: "Só visualizar",
      cancelIcon: faEye,
      onConfirm: async () => {
        await startSessionForAssignment(assignment);
        setPendingViewAssignmentId(null);
      },
    });
  };

  const startSessionForAssignment = async (assignment) => {
    setSelectedExercise(null);
    setCheckinError("");
    setForm((prev) => ({ ...prev, duration: "", weight: "", photo: null }));
    setSelectedChallenge(getActiveCheckinChallenge());
    try {
      const res = await api.post(`/workout-sessions`, { user_workout_plan_id: assignment.id });
      const nowIso = res.data.started_at || new Date().toISOString();
      const session = { id: res.data.id, assignmentId: assignment.id, startedAt: Date.parse(nowIso), exerciseCompletions: {}, exerciseDurations: {}, setsLog: {} };
      setWorkoutSession(session);
      try { localStorage.setItem(`workout_session_${assignment.id}`, JSON.stringify(session)); } catch (_) {}
      setWorkoutModalPreStart(false);
      setWorkoutModalReadonly(false);
      setShowCheckinModal(true);
    } catch (err) {
      console.error('Falha ao iniciar sessão:', err);
      const status = err?.response?.status;
      if (status === 409) {
        // fetch active session and merge with local state
        try {
          const act = await api.get('/workout-sessions/active');
          if (act.data?.active) {
            setWorkoutSession(prev => parseActiveSession(act.data, prev));
            setToast({ type: 'info', message: 'Existe uma sessão em andamento — retomando.' });
            return;
          }
        } catch (_) {}
        setToast({ type: 'error', message: 'Você já possui uma sessão em andamento.' });
        return;
      }

      setToast({ type: 'error', message: 'Não foi possível iniciar sessão de treino no servidor. Tente novamente.' });
    }
  };

  const resolveAvatarUrl = (avatarUrl, fallbackName = "User") => {
    if (!avatarUrl) {
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(fallbackName)}`;
    }
  
    let value = String(avatarUrl).trim();
  
    try {
      value = decodeURIComponent(value);
    } catch {
      // segue com o valor original
    }
  
    value = value.replace(/^\/+/, "");
  
    if (/^https?:\/\//i.test(value)) {
      return value;
    }
  
    const normalizedBase = (apiUrl || "").replace(/\/+$/, "");
    const normalizedPath = value.replace(/^\/+/, "");
  
    return `${normalizedBase}/${normalizedPath}`;
  };

  // Logo personalizado do professor — propagado via contexto para todos os
  // modais/telas que usam useLogoSrc() (ModalHeader, WorkoutSummaryModal).
  // Objeto {dark, light}, não string: light é OPCIONAL (variante clara,
  // R248) — cada consumidor decide sozinho o fallback pro dark quando não
  // houver light, pelo mesmo mecanismo de troca do wordmark padrão.
  // SEM useMemo de propósito: este ponto do componente vem DEPOIS dos early
  // returns de loading (!user?.id / loading acima) — um hook aqui seria
  // condicional (react-hooks/rules-of-hooks) e quebra o build. Objeto novo a
  // cada render, igual o professorLogoSrc (string) que existia antes.
  const professorLogo = {
    dark: me?.professor_logo_url ? `${apiUrl}/${me.professor_logo_url}` : null,
    light: me?.professor_logo_light_url ? `${apiUrl}/${me.professor_logo_light_url}` : null,
  };

  return (
  <LogoContext.Provider value={professorLogo}>
    <div className={styles.page}>
      <header className={styles.header} data-app-header>
        <div className={styles.headerLeft}>
          <div className={styles.headerIdentity}>
        <h1 className={styles.h1}>
        <span className={styles.headerAvatarWrap}>
          <img
            src={resolveAvatarUrl(me?.avatar_url, me?.name)}
            className={styles.headerAvatar}
            alt="Avatar"
            referrerPolicy="no-referrer"
          />
          {me?.role === "aluno" && me?.is_self_service && hasPaidAccess && (
            <span className={styles.avatarPlusBadge} title="Você é assinante Plus">
              <FontAwesomeIcon icon={faCrown} />
            </span>
          )}
        </span>

          {!workoutSession && unreadFeedbacks.length === 0 && !(isMobile && showRenewalBadge) && (
            <span className={styles.headerTextGroup}>
              <strong className={styles.h1Strong}>{getGreeting()}, {me?.name?.split(' ')[0]}</strong>
              <span className={styles.headerDate}>{formatHeaderDate()}</span>
            </span>
          )}
          {showRenewalBadge && (
            <button
              type="button"
              className={styles.renewalBadge}
              onClick={() => setOpenRenewalReminder(true)}
              title="Seu treino está vencido - toque para saber mais"
              aria-label="Seu treino está vencido - toque para saber mais"
            >
              <FontAwesomeIcon icon={faTriangleExclamation} />
            </button>
          )}
          {/* Convite de instalação: só no mobile, por decisão de produto — é
              onde a notificação tem valor real (o aparelho fica no bolso). No
              desktop o push funciona, mas não convidamos: quem trabalha no
              computador já tem o app aberto. NÃO é bug se um professor no
              desktop não vê este badge. O badge do sino abaixo, esse sim
              aparece em qualquer tela, porque aí o app já está instalado e só
              falta autorizar. */}
          {isMobile && showInstallPwaBadge && (
            <button
              type="button"
              className={styles.installPwaBadge}
              onClick={() => setOpenInstallPwa(true)}
              title="Instale o app para receber avisos - toque para saber como"
              aria-label="Instale o app para receber avisos - toque para saber como"
            >
              <FontAwesomeIcon icon={faBellSlash} />
            </button>
          )}
          {showEnablePushBadge && (
            <button
              type="button"
              className={styles.installPwaBadge}
              onClick={handleEnablePush}
              title="Ativar notificações do treino"
              aria-label="Ativar notificações do treino"
            >
              <FontAwesomeIcon icon={faBell} />
            </button>
          )}
          {workoutSession && (
            <button
              type="button"
              className={cn(styles.sessionBadge)}
              onClick={() => {
                // open the running session
                const assignment = assignedWorkouts.find(a => String(a.id) === String(workoutSession.assignmentId));
                if (assignment) {
                  setSelectedWorkoutAssignmentId(String(assignment.id));
                  setWorkoutModalPreStart(false);
                  setWorkoutModalReadonly(false);
                  setShowCheckinModal(true);
                }
              }}
              title={`Treino em andamento${activeAssignmentForSession ? ` • ${(activeAssignmentForSession.custom_name || activeAssignmentForSession.workout_plan?.title || '').replace(/[—–]/g, '-')}` : ''}`}>
              <FontAwesomeIcon icon={faStopwatch} />
              <span>{formatElapsed(elapsedSeconds)}</span>
            </button>
          )}
        </h1>
          </div>
        </div>

        {!isMobile ? (
          <div className={styles.headerRight}>
            {(me?.role === "professor" || me?.role === "admin") && (
              <div className={styles.topTabs} role="tablist" aria-label="Painéis">
                <button
                  type="button"
                  className={cn(styles.tabBtn, activeTopTab === 'teacher' && styles.tabBtnActive)}
                  onClick={() => setActiveTopTab('teacher')}
                  role="tab"
                  aria-selected={activeTopTab === 'teacher'}
                >
                  Acompanhamento
                </button>
                {me?.role === "professor" && (
                  <button
                    type="button"
                    className={cn(styles.tabBtn, activeTopTab === 'analytics' && styles.tabBtnActive)}
                    onClick={() => setActiveTopTab('analytics')}
                    role="tab"
                    aria-selected={activeTopTab === 'analytics'}
                  >
                    <FontAwesomeIcon icon={faChartLine} /> Analytics
                  </button>
                )}
                <button
                  type="button"
                  className={cn(styles.tabBtn, activeTopTab === 'overview' && styles.tabBtnActive)}
                  onClick={() => setActiveTopTab('overview')}
                  role="tab"
                  aria-selected={activeTopTab === 'overview'}
                >
                  Seu treino
                </button>
              </div>
            )}
            {unreadFeedbacks.length > 0 && (
              <button
                onClick={() => setShowFeedbackModal(true)}
                className={styles.iconBtn}
                style={{ position: "relative", marginRight: "4px" }}
                title={`${unreadFeedbacks.length} feedback(s) não lido(s)`}
              >
                <FontAwesomeIcon icon={faCommentDots} />
                <span style={{
                  position: "absolute", top: "-4px", right: "-4px",
                  background: "var(--feedback-warning)", color: "var(--surface-secondary)",
                  fontSize: "10px", fontWeight: 800,
                  borderRadius: "999px", width: "18px", height: "18px",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {unreadFeedbacks.length}
                </span>
              </button>
            )}
            {chatEntry.enabled && (
              <button
                onClick={chatEntry.handleOpen}
                className={styles.iconBtn}
                style={{ position: "relative", marginRight: "4px" }}
                title="Chat da comunidade"
              >
                <FontAwesomeIcon icon={faComments} />
                {chatEntry.unreadCount > 0 && (
                  <span style={{
                    position: "absolute", top: "-4px", right: "-4px",
                    background: chatEntry.hasMentionUnread ? "var(--feedback-warning, #ef9f27)" : "var(--blue)",
                    color: chatEntry.hasMentionUnread ? "#1a1206" : "#fff",
                    fontSize: "10px", fontWeight: 800,
                    borderRadius: "999px", minWidth: "18px", height: "18px",
                    padding: "0 3px",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                  title={chatEntry.hasMentionUnread ? "Você foi mencionado" : undefined}
                  >
                    {chatEntry.unreadCount > 99 ? "99+" : chatEntry.unreadCount}
                  </span>
                )}
              </button>
            )}
            <div className={styles.headerStats}>
              <button
                className={styles.headerStatPill}
                onClick={() => setShowPointsModal(true)}
                title="Ver histórico de pontos"
              >
                <span className={styles.headerStatValue}><span style={{color:'var(--accent)'}}>{totalPoints}</span> pts</span>
                {pointsToday > 0 && <span className={styles.headerStatToday}>+{pointsToday} hoje</span>}
              </button>
              <button
                className={cn(styles.headerStatPill, styles.clickable)}
                onClick={() => {
                  setRankingTab(rankingPeriods[rankingPillIdx].tab);
                  setShowRankingModal(true);
                }}
                title={`Ver ranking - ${rankingPeriods[rankingPillIdx].label}`}
              >
                <span className={styles.headerStatLabel}>{rankingPeriods[rankingPillIdx].label}</span>
                <span className={styles.headerStatValue}><span style={{color:'var(--accent)'}}>{rankingPeriods[rankingPillIdx].pos}</span> <span className={styles.rankingArrow}>→</span></span>
                <span className={styles.rankingDots} aria-hidden="true">
                  {rankingPeriods.map((_, i) => (
                    <span key={i} className={cn(styles.rankingDot, i === rankingPillIdx && styles.rankingDotActive)} />
                  ))}
                </span>
              </button>
            </div>

            <ActionMenu
              isMobile={false}
              roleUser={me?.role}
              label="Menu"
              options={menuOptions}
            />
            <button
              type="button"
              className={cn(styles.iconBtn, styles.themeToggleBtn)}
              onClick={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                setTheme(theme === 'dark' ? 'light' : 'dark', { x: r.left + r.width / 2, y: r.top + r.height / 2 });
              }}
              aria-label={theme === 'dark' ? 'Ativar tema claro' : 'Ativar tema escuro'}
              title={theme === 'dark' ? 'Tema claro' : 'Tema escuro'}
            >
              {theme === 'dark' ? <SunIcon /> : <FontAwesomeIcon icon={faMoon} />}
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {unreadFeedbacks.length > 0 && (
              <button
                onClick={() => setShowFeedbackModal(true)}
                className={styles.iconBtn}
                aria-label="Feedback do professor"
                title="Feedback do professor"
                style={{ position: "relative" }}
              >
                <FontAwesomeIcon icon={faCommentDots} />
                <span style={{
                  position: "absolute", top: "-4px", right: "-4px",
                  background: "var(--feedback-warning)", color: "var(--surface-secondary)",
                  fontSize: "10px", fontWeight: 800,
                  borderRadius: "999px", width: "18px", height: "18px",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  lineHeight: 1,
                }}>
                  {unreadFeedbacks.length}
                </span>
              </button>
            )}
            {chatEntry.enabled && (
              <button
                onClick={chatEntry.handleOpen}
                className={styles.iconBtn}
                aria-label="Chat da comunidade"
                title="Chat da comunidade"
                style={{ position: "relative" }}
              >
                <FontAwesomeIcon icon={faComments} />
                {chatEntry.unreadCount > 0 && (
                  <span style={{
                    position: "absolute", top: "-4px", right: "-4px",
                    background: chatEntry.hasMentionUnread ? "var(--feedback-warning, #ef9f27)" : "var(--blue)",
                    color: chatEntry.hasMentionUnread ? "#1a1206" : "#fff",
                    fontSize: "10px", fontWeight: 800,
                    borderRadius: "999px", minWidth: "18px", height: "18px",
                    padding: "0 3px",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    lineHeight: 1,
                  }}
                  title={chatEntry.hasMentionUnread ? "Você foi mencionado" : undefined}
                  >
                    {chatEntry.unreadCount > 99 ? "99+" : chatEntry.unreadCount}
                  </span>
                )}
              </button>
            )}
            <button
              type="button"
              className={cn(styles.iconBtn, styles.themeToggleBtn)}
              onClick={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                setTheme(theme === 'dark' ? 'light' : 'dark', { x: r.left + r.width / 2, y: r.top + r.height / 2 });
              }}
              aria-label={theme === 'dark' ? 'Ativar tema claro' : 'Ativar tema escuro'}
              title={theme === 'dark' ? 'Tema claro' : 'Tema escuro'}
            >
              {theme === 'dark' ? <SunIcon /> : <FontAwesomeIcon icon={faMoon} />}
            </button>
            <button onClick={() => setShowMenu(true)} className={cn(styles.iconBtn, styles.headerMenuBtn)} aria-label="Abrir menu" title="Menu">
              <FontAwesomeIcon icon={faBars} />
            </button>
          </div>
        )}
      </header>

      {isMobile && (
        <div className={styles.mobileStatBar}>
          <button className={cn(styles.mobileStatItem, styles.clickable)} onClick={() => setShowPointsModal(true)}>
            <span className={styles.mobileStatLabel}>Pontos</span>
            <span className={styles.mobileStatValue}><span style={{color:'var(--accent)'}}>{totalPoints}</span> <span className={styles.rankingArrow}>→</span></span>
            {pointsToday > 0 && <span className={styles.mobileStatBadge}>+{pointsToday}</span>}
          </button>
          <div className={styles.mobileStatDivider} />
          <button
            className={cn(styles.mobileStatItem, styles.clickable)}
            onClick={() => {
              setRankingTab(rankingPeriods[rankingPillIdx].tab);
              setShowRankingModal(true);
            }}
            title={`Ver ranking - ${rankingPeriods[rankingPillIdx].label}`}
          >
            <span className={styles.mobileStatLabel}>{rankingPeriods[rankingPillIdx].label}</span>
            <span className={styles.mobileStatValue}><span style={{color:'var(--accent)'}}>{rankingPeriods[rankingPillIdx].pos}</span> <span className={styles.rankingArrow}>→</span></span>
            <span className={styles.rankingDots} aria-hidden="true">
              {rankingPeriods.map((_, i) => (
                <span key={i} className={cn(styles.rankingDot, i === rankingPillIdx && styles.rankingDotActive)} />
              ))}
            </span>
          </button>
        </div>
      )}

      {loadError && (
        <div className={styles.loadErrorCard}>
          <span>{loadError}</span>
          <button type="button" className={cn(styles.btn, styles.btnSecondary)} onClick={loadData}>
            Tentar novamente
          </button>
        </div>
      )}

      {isMobile && isManagerRole && (
        <div className={styles.mobileProfTabs} role="tablist">
          <button
            className={styles.mobileProfTab + (activeTopTab === 'teacher' ? ' ' + styles.mobileProfTabActive : '')}
            onClick={() => setActiveTopTab('teacher')}
          >
            Acompanhamento
          </button>
          {me?.role === "professor" && (
            <button
              className={styles.mobileProfTab + (activeTopTab === 'analytics' ? ' ' + styles.mobileProfTabActive : '')}
              onClick={() => setActiveTopTab('analytics')}
            >
              <FontAwesomeIcon icon={faChartLine} /> Analytics
            </button>
          )}
          <button
            className={styles.mobileProfTab + (activeTopTab === 'overview' ? ' ' + styles.mobileProfTabActive : '')}
            onClick={() => setActiveTopTab('overview')}
          >
            Seu treino
          </button>
        </div>
      )}
      {isMobile && isManagerRole && activeTopTab === 'teacher' ? (
        me?.role === "admin" ? (
          <AdminDashboard
            onOpenSendEmail={() => setShowSendEmailModal(true)}
            onOpenSendPush={() => setShowAdminPushModal(true)}
            onOpenAiUsage={() => setShowAiUsageModal(true)}
            onOpenManageUsers={() => setOpenUsersModal(true)}
            onOpenPaymentsAdmin={() => setShowPaymentsAdminModal(true)}
            onOpenInviteProfessor={() => { setInviteProfessorKind("professor"); setShowInviteProfessor(true); }}
            onOpenOrganizations={() => setShowOrganizationsAdmin(true)}
          />
        ) : (
          <TeacherDashboard apiUrl={apiUrl} user={me} hideStudents={me?.role === "professor" && !hasProfessorBillingAccess} hasProfessorBillingAccess={hasProfessorBillingAccess} lastSubscription={lastSubscription} onOpenOrgPayments={() => setShowPaymentsModal(true)} />
        )
      ) : isMobile && activeTopTab === 'analytics' && me?.role !== "admin" ? (
        <div style={{ padding: '1.5rem' }}>
          <TeacherAnalyticsDashboard apiUrl={apiUrl} user={me} />
        </div>
      ) : isMobile ? (
        <>
          {/* Treinos (mobile) — PRIMEIRO bloco de conteúdo. O clima e a frase
              motivacional foram pra baixo da seção: a ação principal do app
              precisa estar na primeira dobra (achado P1 da auditoria mobile
              de 06/09/2026). */}
          <section ref={workoutSectionRef} className={styles.workoutFocus} aria-label="Treinos disponíveis">
            {heroWorkout && (() => {
              const a = heroWorkout.kind;
              const assignment = heroWorkout.assignment;
              const isDepleted = availableCheckinsToday <= 0;
              const eyebrow = a === "inProgress" ? "Treino em andamento"
                            : a === "today"      ? "Seu treino de hoje"
                            :                      "Próximo treino sugerido";
              const ctaLabel = isDepleted        ? "Ver treino"
                            : a === "inProgress" ? "Continuar treino"
                            :                      "Começar treino";
              const exCount = (assignment.items || []).length;
              return (
                <div className={styles.todayHero}>
                  <span className={styles.todayHeroEyebrow}>
                    <FontAwesomeIcon icon={a === "inProgress" ? faStopwatch : faDumbbell} />
                    {eyebrow}
                  </span>
                  <h2 className={styles.todayHeroTitle}>{getWorkoutTitle(assignment)}</h2>
                  {exCount > 0 && (
                    <span className={styles.todayHeroMeta}>
                      {exCount} exercício{exCount > 1 ? "s" : ""}
                    </span>
                  )}
                  <button
                    type="button"
                    className={cn(styles.btn, styles.btnPrimary, styles.todayHeroCta)}
                    onClick={() => openWorkoutCta(assignment)}
                  >
                    <FontAwesomeIcon icon={isDepleted ? faLock : faPlay} />
                    {ctaLabel}
                  </button>
                  {isDepleted && (
                    <span className={styles.todayHeroLimit}>
                      Limite diário de registros atingido - dá pra consultar, mas não registrar.
                    </span>
                  )}

                  {/* Progresso da semana logo abaixo do CTA — achado P2 da
                      auditoria: os anéis eram desktop-only, ausentes
                      justamente onde o uso diário acontece. Fica DEPOIS do
                      botão de propósito: informa a decisão sem empurrar a
                      ação principal pra fora da primeira dobra. O texto
                      mostra o que FALTA, não só o que foi feito (Goal
                      Gradient — a motivação cresce perto da meta). */}
                  <div className={styles.todayHeroProgress}>
                    <ActivityRings
                      compact
                      size={84}
                      weeklyProgress={weeklyRingPct}
                      dailyProgress={dailyRingPct}
                      challengeProgress={avgChallengePct}
                      weeklyCheckins={checkinsThisWeek}
                      weeklyGoal={weeklyGoalN}
                      todayCheckins={checkinsToday}
                    />
                    <div className={styles.todayHeroProgressText}>
                      <strong>{checkinsThisWeek} de {weeklyGoalN}</strong> treinos nesta semana
                      <span>
                        {checkinsThisWeek >= weeklyGoalN
                          ? "Meta da semana batida — o resto é lucro."
                          : `Falta${weeklyGoalN - checkinsThisWeek > 1 ? "m" : ""} ${weeklyGoalN - checkinsThisWeek} pra bater a meta.`}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className={styles.workoutFocusHeaderRow}>
              <h2 className={styles.sectionTitle}>{heroWorkout ? "Outros treinos" : "Treinos disponíveis"}</h2>
              <span className={styles.workoutCount}>{assignedWorkouts.length} ativos</span>
            </div>

            {assignedWorkouts.length ? (
              <div className={styles.workoutCompactGrid}>
                {(() => {
                  const todayIdx = (new Date().getDay() + 6) % 7;
                  // Remove do grid o treino que já aparece no hero acima —
                  // senão o mesmo card apareceria duas vezes na mesma tela.
                  const sorted = sortAssignmentsBySuggestion(
                    heroWorkout
                      ? assignedWorkouts.filter((a) => a.id !== heroWorkout.assignment.id)
                      : assignedWorkouts
                  );

                  // Detecta quantos cards são "half-width" (ou seja, NÃO vão ganhar
                  // largura total). Precisa espelhar exatamente a mesma regra usada lá
                  // embaixo pra aplicar workoutCardCompactToday/InProgress — senão a
                  // paridade do pool fica errada e o card errado vira "full" sozinho.
                  // "Hoje" só vira full-width quando NÃO há treino em andamento
                  // (workoutCardCompactToday exige isTodaySuggestion && !workoutSession);
                  // com uma sessão ativa, o card de hoje continua normal e por isso
                  // deve continuar contando no pool de half-width.
                  const halfWidthCards = sorted.filter(a => {
                    const isToday = Number(a.day_of_week) === todayIdx;
                    const isIP = workoutSession && String(workoutSession.assignmentId) === String(a.id);
                    const isFullWidth = (isToday && !workoutSession) || isIP;
                    return !isFullWidth;
                  });
                  const lastHalfWidthId = halfWidthCards.length % 2 === 1
                    ? halfWidthCards[halfWidthCards.length - 1]?.id
                    : null;

                  return sorted.map((assignment) => {
                    const isTodaySuggestion = Number(assignment.day_of_week) === todayIdx;
                    const isInProgress = workoutSession && String(workoutSession.assignmentId) === String(assignment.id);
                    const isLastAlone = assignment.id === lastHalfWidthId;

                    // Última execução relativa (comparação por data calendário, não ms brutos)
                    let lastExecLabel = null;
                    if (assignment.last_executed_at) {
                      const last = new Date(assignment.last_executed_at);
                      const todayMid = new Date(); todayMid.setHours(0,0,0,0);
                      const lastMid  = new Date(last.getFullYear(), last.getMonth(), last.getDate());
                      const diff = Math.round((todayMid - lastMid) / 86400000);
                      if (diff === 0)       lastExecLabel = "Última: hoje";
                      else if (diff === 1)  lastExecLabel = "Última: ontem";
                      else if (diff < 7)   lastExecLabel = `Última: ${diff}d atrás`;
                      else if (diff < 14)  lastExecLabel = "Última: 1 sem. atrás";
                      else                 lastExecLabel = `Última: ${Math.floor(diff/7)}sem. atrás`;
                    }

                    const dayLabel = isTodaySuggestion
                      ? "Hoje"
                      : assignment.day_of_week != null
                        ? getDayName(Number(assignment.day_of_week))
                        : "Sem dia fixo";

                    const isDepleted = availableCheckinsToday <= 0;

                    return (
                      <article
                        key={assignment.id}
                        className={cn(
                          styles.workoutCardCompact,
                          isTodaySuggestion && !workoutSession && styles.workoutCardCompactToday,
                          isInProgress && styles.workoutCardCompactInProgress,
                          isLastAlone && styles.workoutCardCompactFull,
                          isDepleted && styles.workoutCardCompactDepleted,
                        )}
                        role="button"
                        tabIndex={0}
                        onClick={() => editingDayId !== assignment.id && openWorkoutCta(assignment)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (editingDayId !== assignment.id) openWorkoutCta(assignment); }
                        }}
                        aria-label={isDepleted ? `Abrir treino: ${getWorkoutTitle(assignment)} (somente leitura, limite diário atingido)` : `Abrir treino: ${getWorkoutTitle(assignment)}`}
                      >
                        <div className={styles.wcAccent} />
                        <div className={styles.wcBody}>
                          <span className={styles.wcDayBadge} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            {isInProgress
                              ? <><FontAwesomeIcon icon={faStopwatch} style={{ marginRight: 4 }} />Em andamento</>
                              : editingDayId === assignment.id
                                ? (
                                  <select
                                    autoFocus
                                    className={styles.daySelect}
                                    defaultValue={assignment.day_of_week ?? ""}
                                    onClick={e => e.stopPropagation()}
                                    onKeyDown={e => { if (e.key === "Escape") setEditingDayId(null); e.stopPropagation(); }}
                                    onChange={e => { e.stopPropagation(); patchWorkoutDay(assignment.id, e.target.value === "" ? null : Number(e.target.value)); }}
                                    onBlur={() => setEditingDayId(null)}
                                  >
                                    <option value="">Sem dia fixo</option>
                                    {["Segunda","Terça","Quarta","Quinta","Sexta","Sábado","Domingo"].map((d, i) => (
                                      <option key={i} value={i}>{d}</option>
                                    ))}
                                  </select>
                                )
                                : (
                                  <>
                                    {dayLabel}
                                    {!isInProgress && !me?.professor_id && (
                                      <button
                                        type="button"
                                        title="Alterar dia"
                                        onClick={e => { e.stopPropagation(); e.preventDefault(); setEditingDayId(assignment.id); }}
                                        style={{ background: "none", border: "none", padding: "10px 12px", margin: "-10px -8px", cursor: "pointer", color: "var(--text-0-40)", lineHeight: 1, fontSize: 13, touchAction: "manipulation" }}
                                      >
                                        <FontAwesomeIcon icon={faPen} />
                                      </button>
                                    )}
                                  </>
                                )
                            }
                          </span>
                          <h3 className={styles.wcTitle}>{getWorkoutTitle(assignment)}</h3>
                          <div className={styles.wcMeta}>
                            {lastExecLabel
                              ? <span className={styles.wcLastExec}>{lastExecLabel}</span>
                              : <span className={styles.wcLastNever}>Nunca executado</span>
                            }
                          </div>
                        </div>
                        <div className={styles.wcArrow}>
                          {isDepleted ? <FontAwesomeIcon icon={faLock} style={{ fontSize: 12 }} /> : "›"}
                        </div>
                      </article>
                    );
                  });
                })()}
              </div>
            ) : (
              <div className={styles.emptyWorkouts}>
                <FontAwesomeIcon icon={faDumbbell} className={styles.emptyWorkoutsIcon} />
                <p className={styles.emptyWorkoutsTitle}>Nenhuma ficha vinculada ainda</p>
                <p className={styles.emptyWorkoutsSub}>
                  {me?.is_self_service && me?.role === "aluno"
                    ? "Gere sua ficha personalizada em segundos, ou peça para seu professor vincular uma."
                    : "Quando seu professor vincular uma ficha ao seu perfil, ela aparecerá aqui como ponto de partida do treino."}
                </p>
                {me?.is_self_service && me?.role === "aluno" && (
                  <button
                    type="button"
                    className={cn(styles.btn, styles.btnPrimary)}
                    style={{ marginTop: 6 }}
                    onClick={handleAiWorkoutClick}
                  >
                    <FontAwesomeIcon icon={faWandMagicSparkles} />Gerar meu treino personalizado
                  </button>
                )}
              </div>
            )}
          </section>

          {/* Clima + frase motivacional — desceram da primeira dobra: são
              contexto, não a ação principal (ver comentário acima). */}
          <WeatherPanel isMobile={true} />

          <section ref={evolutionSectionRef} className={styles.sectionHeading}>
            <div>
              <h2 className={styles.sectionTitle}>Sua evolução</h2>
            </div>

          </section>
          <>
              <div className={styles.streakCardWrap}>
                <StreakCard checkins={checkins} weeklyGoal={Number(me?.weekly_goal || 3)} series={weeklyCadenceSeries} />
              </div>
              <div className={styles.metricsGrid}>
                <WeeklyVolumeChart checkins={checkins} weeks={CHART_WEEKS} weeklyGoal={Number(me?.weekly_goal || 3)} cadenceSeries={weeklyCadenceSeries} durationSeries={weeklyDurationSeries} />
                <WeeklyLoadChart volumeSeries={weeklyVolumeSeries} weeks={CHART_WEEKS} />
              </div>
            </>

        </>
      ) : (
        <>
      {activeTopTab === 'overview' && (
      <section className={styles.heroGrid}>
        <div className={styles.heroCard}>
          <div className={styles.heroCardInner}>
            <div className={styles.heroContent}>
              <span className={styles.heroEyebrow}>{heroContent.eyebrow}</span>
              <h2 className={styles.heroTitle}>{heroContent.titleNode}</h2>
              <p className={styles.heroDescription}>{heroContent.desc}</p>

              <div className={styles.heroMeta}>
                {(() => {
                  const level = getUserLevel(totalPoints);
                  return (
                    <span className={styles.levelHeroPill}>
                      <FontAwesomeIcon icon={level.icon} /> {level.tier}
                      {level.next && (
                        <span style={{ opacity: 0.65, fontWeight: 600, fontSize: 11 }}>
                          {' '}· {level.next - totalPoints} pts para {
                            getUserLevel(level.next).tier
                          }
                        </span>
                      )}
                    </span>
                  );
                })()}
                <span className={styles.heroMetaPill}>{completedChallenges} desafios concluídos</span>
                <span className={styles.heroMetaPill}>{availableCheckinsToday} treinos disponíveis hoje</span>
              </div>

              {/* Weather + motivation */}
              <WeatherPanel isMobile={isMobile} />

            </div>

            {/* Activity Rings — desktop only */}
            <ActivityRings
              weeklyProgress={weeklyRingPct}
              dailyProgress={dailyRingPct}
              challengeProgress={avgChallengePct}
              weeklyCheckins={checkinsThisWeek}
              weeklyGoal={weeklyGoalN}
              todayCheckins={checkinsToday}
            />
          </div>
        </div>
      </section>
      )}

      {activeTopTab === 'overview' && (
      <section className={styles.sectionHeading}>
        <div>
          <span className={styles.sectionEyebrow}>Acompanhamento</span>
          <h2 className={styles.sectionTitle}>Sua evolução</h2>
        </div>

      </section>
      )}
      {activeTopTab === 'overview' && (
        <>
          <div className={styles.streakCardWrap}>
            <StreakCard checkins={checkins} weeklyGoal={Number(me?.weekly_goal || 3)} series={weeklyCadenceSeries} />
          </div>
          <section className={styles.metricsGrid}>
            <WeeklyVolumeChart checkins={checkins} weeks={CHART_WEEKS} weeklyGoal={Number(me?.weekly_goal || 3)} cadenceSeries={weeklyCadenceSeries} durationSeries={weeklyDurationSeries} />
            <WeeklyLoadChart volumeSeries={weeklyVolumeSeries} weeks={CHART_WEEKS} />
          </section>
        </>
      )}

      {activeTopTab === 'teacher' && (
        me?.role === "admin" ? (
          <AdminDashboard
            onOpenSendEmail={() => setShowSendEmailModal(true)}
            onOpenSendPush={() => setShowAdminPushModal(true)}
            onOpenAiUsage={() => setShowAiUsageModal(true)}
            onOpenManageUsers={() => setOpenUsersModal(true)}
            onOpenPaymentsAdmin={() => setShowPaymentsAdminModal(true)}
            onOpenInviteProfessor={() => { setInviteProfessorKind("professor"); setShowInviteProfessor(true); }}
            onOpenOrganizations={() => setShowOrganizationsAdmin(true)}
          />
        ) : (
          <TeacherDashboard apiUrl={apiUrl} user={me} hideStudents={me?.role === "professor" && !hasProfessorBillingAccess} hasProfessorBillingAccess={hasProfessorBillingAccess} lastSubscription={lastSubscription} onOpenOrgPayments={() => setShowPaymentsModal(true)} />
        )
      )}

      {activeTopTab === 'analytics' && me?.role !== "admin" && (
        <div style={{ padding: '1.5rem' }}>
          <TeacherAnalyticsDashboard apiUrl={apiUrl} user={me} />
        </div>
      )}

      {activeTopTab === 'overview' && (
      <section className={styles.metricsGrid}>
        <Card>
          <strong className={styles.bigStat}>{checkinsToday}</strong>
          <p className={styles.statLabel}>Treinos feitos hoje</p>
        </Card>
        <Card>
          <strong className={styles.bigStat}>{availableCheckinsToday}</strong>
          <p className={styles.statLabel}>Treinos restantes hoje</p>
        </Card>
      </section>
      )}

      {activeTopTab === 'overview' && me?.role === "aluno" && (!me?.is_self_service || hasPaidAccess) && (
        <DietBannerCard onOpen={() => setOpenNutrition(true)} />
      )}

      {false && (
      <section className={styles.sectionHeading}>
        <div>
          <span className={styles.sectionEyebrow}>Desafios</span>
          <h2 className={styles.sectionTitle}>Seus desafios e oportunidades</h2>
        </div>
        <p className={styles.sectionText}>
          Cada card agora destaca status, progresso e próximas ações de forma mais objetiva.
        </p>
      </section>
      )}

        </>
      )}

      {!isMobile && activeTopTab === 'overview' && (
      <section className={styles.workoutFocus}>
        <div className={styles.workoutFocusHeader}>
          <div>
            <span className={styles.sectionEyebrow}>Treinos</span>
            <h2 className={styles.sectionTitle}>Treinos disponiveis</h2>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span className={styles.workoutCount}>{assignedWorkouts.length} ativos</span>
          </div>
        </div>

        {assignedWorkouts.length ? (
          <div className={cn(styles.workoutCarouselShell, assignedWorkouts.length > 4 && styles.workoutCarouselScrollable)}>
            <div className={cn(styles.workoutCardsGrid, assignedWorkouts.length <= 4 && styles.workoutCardsFit)}>
              {sortAssignmentsBySuggestion(assignedWorkouts).map((assignment) => {
                const workoutItems = sortWorkoutItems(assignment.items || []);
                const previewItems = workoutItems.slice(0, 3);

                const todayIdx = (new Date().getDay() + 6) % 7; // Monday=0..Sunday=6
                const isTodaySuggestion = Number(assignment.day_of_week) === todayIdx;
                const isInProgress = workoutSession && String(workoutSession.assignmentId) === String(assignment.id);
                return (
                  <article
                    key={assignment.id}
                    className={cn(styles.workoutCard, isTodaySuggestion && !workoutSession && styles.workoutCardToday, isInProgress && styles.workoutCardInProgress, availableCheckinsToday <= 0 && styles.workoutCardDepleted)}
                    role="button"
                    tabIndex={0}
                    onClick={() => editingDayId !== assignment.id && openWorkoutCta(assignment)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (editingDayId !== assignment.id) openWorkoutCta(assignment);
                      }
                    }}
                    aria-label={`Abrir e registrar treino: ${getWorkoutTitle(assignment)}`}
                  >
                    <span
                      className={styles.stretchedLink}
                      aria-hidden="true"
                      style={{ pointerEvents: "none" }}
                    />
                    <div className={styles.workoutCardTop}>
                      <div>
                        <h3 className={styles.workoutCardTitle}>{getWorkoutTitle(assignment)}</h3>
                        <div className={styles.workoutBadgeRow} style={{ position: "relative", zIndex: 2 }}>
                          {/* badge de dia — sempre mostra lápis (inclui "Hoje") */}
                          {editingDayId === assignment.id
                            ? (
                              <span className={isTodaySuggestion ? styles.todayBadge : styles.suggestionBadge}
                                style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                <select
                                  autoFocus
                                  className={styles.daySelect}
                                  defaultValue={assignment.day_of_week ?? ""}
                                  onClick={e => e.stopPropagation()}
                                  onKeyDown={e => { if (e.key === "Escape") setEditingDayId(null); e.stopPropagation(); }}
                                  onChange={e => { e.stopPropagation(); patchWorkoutDay(assignment.id, e.target.value === "" ? null : Number(e.target.value)); }}
                                  onBlur={() => setEditingDayId(null)}
                                >
                                  <option value="">Sem dia fixo</option>
                                  {["Segunda","Terça","Quarta","Quinta","Sexta","Sábado","Domingo"].map((d, i) => (
                                    <option key={i} value={i}>{d}</option>
                                  ))}
                                </select>
                              </span>
                            )
                            : (
                              <span className={isTodaySuggestion ? styles.todayBadge : styles.suggestionBadge}
                                style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                {isTodaySuggestion ? "Hoje" : (assignment.day_of_week != null ? `Sugerido: ${getDayName(Number(assignment.day_of_week))}` : "Sem dia fixo")}
                                {!me?.professor_id && (
                                  <button
                                    type="button"
                                    title="Alterar dia"
                                    onClick={e => { e.stopPropagation(); e.preventDefault(); setEditingDayId(assignment.id); }}
                                    style={{ background: "none", border: "none", padding: "8px 10px", margin: "-4px -6px", cursor: "pointer", color: "var(--text-0-40)", lineHeight: 1, fontSize: 12, position: "relative", zIndex: 3 }}
                                  >
                                    <FontAwesomeIcon icon={faPen} />
                                  </button>
                                )}
                              </span>
                            )
                          }
                          {workoutSession && String(workoutSession.assignmentId) === String(assignment.id) && (
                            <span className={styles.inProgressBadge} aria-label="Treino em andamento">
                              <FontAwesomeIcon icon={faStopwatch} /> Em andamento
                            </span>
                          )}
                        </div>
                        <p className={styles.workoutCardMeta}>
                          {workoutItems.length} exercicios
                        </p>
                      </div>
                      <div className={styles.workoutCardRight}>
                        <FontAwesomeIcon icon={faDumbbell} className={styles.workoutCardIcon} />
                      </div>
                    </div>

                    <div className={styles.workoutPreviewList}>
                      {previewItems.map((item, index) => {
                        const summary = [
                          item.sets_planned ? `${item.sets_planned}x` : null,
                          item.reps_planned || null,
                          item.target_rir != null ? `deixe ${item.target_rir} reps na reserva` : null,
                          item.weight_planned ? `${item.weight_planned} kg` : null,
                          item.duration_planned ? `${item.duration_planned} min` : null,
                        ]
                          .filter(Boolean)
                          .join(" - ");

                        return (
                          <div key={item.id || `${item.exercise_item_id}-${index}`} className={styles.workoutPreviewItem}>
                            <div className={styles.workoutPreviewNameRow}>
                              <span className={styles.workoutPreviewIndex}>{index + 1}</span>
                              <span className={styles.workoutPreviewName}>{item.exercise_item?.name || `Exercicio ${index + 1}`}</span>
                            </div>
                            {summary && <span className={styles.workoutPreviewSummary}>{summary}</span>}
                          </div>
                        );
                      })}
                      {workoutItems.length > previewItems.length && (
                        <div className={styles.workoutPreviewMore}>+{workoutItems.length - previewItems.length} exercicios na ficha</div>
                      )}
                    </div>

                    <button
                      type="button"
                      className={cn(
                        styles.btn,
                        isTodaySuggestion && !workoutSession ? styles.btnPrimary : styles.btnOutline,
                        styles.btnFull
                      )}
                      onClick={(e) => { e.stopPropagation(); openWorkoutCta(assignment); }}
                    >
                      {availableCheckinsToday <= 0
                        ? "Ver ficha (somente leitura)"
                        : isTodaySuggestion && !workoutSession
                          ? "Iniciar treino de hoje"
                          : "Ver ficha"}
                    </button>
                  </article>
                );
              })}
            </div>
          </div>
        ) : (
          <div className={styles.emptyWorkouts}>
            <FontAwesomeIcon icon={faDumbbell} className={styles.emptyWorkoutsIcon} />
            <p className={styles.emptyWorkoutsTitle}>Nenhuma ficha vinculada ainda</p>
            <p className={styles.emptyWorkoutsSub}>
              {me?.is_self_service && me?.role === "aluno"
                ? "Gere sua ficha personalizada em segundos, ou peça para seu professor vincular uma."
                : "Quando seu professor vincular uma ficha ao seu perfil, ela aparecerá aqui como ponto de partida do treino."}
            </p>
            {me?.is_self_service && me?.role === "aluno" && (
              <button
                type="button"
                className={cn(styles.btn, styles.btnPrimary)}
                style={{ marginTop: 6 }}
                onClick={handleAiWorkoutClick}
              >
                <FontAwesomeIcon icon={faWandMagicSparkles} />Gerar meu treino personalizado
              </button>
            )}            
          </div>
        )}
      </section>
      )}

      {showChallengesModal && (
        <Modal
          onClose={() => setShowChallengesModal(false)}
          onConfirm={() => setShowChallengesModal(false)}
          confirmLabel="Fechar"
          hideCancelBtn={true}
          isMobile={isMobile}
          title="Desafios"
        >
          {workoutModalPreStart && selectedWorkoutAssignment && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <button
                type="button"
                className={cn(styles.btn, styles.btnPrimary)}
                onClick={() => startSessionForAssignment(selectedWorkoutAssignment)}
              >
                Iniciar treino
              </button>
              <button
                type="button"
                className={cn(styles.btn, styles.btnSecondary)}
                onClick={() => { setWorkoutModalPreStart(false); setWorkoutModalReadonly(true); }}
              >
                Somente visualizar
              </button>
            </div>
          )}
      {/* Cabeçalho de desafios (desktop): pontos + ranking */}
      {!isMobile && (
        <div className={styles.topRow} style={{ marginBottom: 12 }}>
          <div className={styles.topLeft}>
            <PointsCard total={totalPoints} today={pointsToday} onClick={() => setShowPointsModal(true)} />
          </div>
          <button
            className={styles.rankingCard}
            onClick={() => setShowRankingModal(true)}
            type="button"
          >
            <span className={styles.rankingLabel}>Ranking</span>
            <strong className={styles.rankingValue}>{myRankingPosition}</strong>
            {topMicroLabel && <span className={styles.rankingSub}>{topMicroLabel}</span>}
            <span className={styles.rankingCta}>
              Ver ranking <FontAwesomeIcon icon={faChevronRight} />
            </span>
          </button>
        </div>
      )}

      {/* Cabeçalho de desafios (mobile): dois cards (Pontos + Ranking) */}
      {isMobile && (
        <div className={styles.topRow} style={{ marginBottom: 12 }}>
          <div>
            <PointsCard total={totalPoints} today={pointsToday} onClick={() => setShowPointsModal(true)} />
          </div>
          <button
            className={styles.rankingCard}
            onClick={() => setShowRankingModal(true)}
            type="button"
          >
            <span className={styles.rankingLabel}>Ranking</span>
            <strong className={styles.rankingValue}>{myRankingPosition}</strong>
            {topMicroLabel && <span className={styles.rankingSub}>{topMicroLabel}</span>}
            <span className={styles.rankingCta}>Ver ranking <FontAwesomeIcon icon={faChevronRight} /></span>
          </button>
        </div>
      )}

      {me?.role === "aluno" && (
        <div className={styles.friendChallengeCta}>
          <span className={styles.friendChallengeCtaIcon}>
            <FontAwesomeIcon icon={faUserGroup} />
          </span>
          <div className={styles.friendChallengeCtaText}>
            <span className={styles.friendChallengeCtaTitle}>Desafio com amigos</span>
            <span className={styles.friendChallengeCtaSub}>Crie um desafio privado e convide até 20 pessoas por email</span>
          </div>
          <button
            type="button"
            className={styles.friendChallengeCtaBtn}
            onClick={() => setShowFriendChallengeModal(true)}
          >
            <FontAwesomeIcon icon={faPlus} />
            Criar
          </button>
        </div>
      )}

      <div className={styles.challengeGrid}>
        {!visibleChallenges.length && (
          <Card>
            <div className={styles.emptyChallenges}>
              <h3 className={styles.h3}>Nenhum desafio disponível no momento</h3>
              <p className={styles.muted}>
                Assim que houver um desafio ativo, ele aparecerá aqui para você acompanhar e fazer check-in.
              </p>
              {me?.role === "professor" && (
                <button
                  type="button"
                  className={cn(styles.btn, styles.btnPrimary)}
                  onClick={() => setShowCreateModal(true)}
                >
                  Criar desafio
                </button>
              )}

              {availableCheckinsToday <= 0 && (
                <div className={cn(styles.pill, styles.pillWarn)} style={{ marginBottom: 8 }}>
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                  <span>Limite diário de 2 registros atingido. Novos registros estarão disponíveis amanhã.</span>
                </div>
              )}
            </div>
          </Card>
        )}
        {visibleChallenges.map((c) => {
          const challengeId = c.challenge_id;

          const diasRestantes = daysRemaining(c.end_date);
          const diasParaIniciar = daysUntilStart(c.start_date);
          const temporal = challengeTemporalStatus(c);
          const progressPercent = calcProgress(c);

          return (
            <Card key={challengeId}>
              <div className={styles.cardHead}>
                <div className={styles.cardTitleWrap}>
                  <span className={styles.cardEyebrow}>
                    {(() => {
                      if (c.isCreator) return "Criado por você";
                      if (temporal === "ended") return "Encerrado";
                      if (temporal === "upcoming") return c.isParticipant ? "Inscrito · inicia em breve" : "Em breve";
                      return c.isParticipant ? "Em andamento" : "Disponível";
                    })()}
                  </span>
                  <h3 className={styles.h3}>{c.title}</h3>
                  {c.isCreator && (
                    <span
                      className={cn(
                        styles.visibilityPill,
                        c.is_public ? styles.visibilityPillPublic : styles.visibilityPillPrivate
                      )}
                    >
                      <FontAwesomeIcon icon={c.is_public ? faGlobe : faLock} />
                      {c.is_public ? "Público" : (me?.role === "admin" ? "Privado (todos os alunos)" : "Somente para seu grupo")}
                    </span>
                  )}
                </div>
                <div className={styles.cardHeadRight}>
                  <StatusBadge completed={c.completed} challenge={c} />
                  {(user?.role === "admin" || user?.role === "professor" || c.isCreator || (c.is_private_invite && c.isParticipant)) && (
                    <ActionMenu
                      isMobile={isMobile}
                      iconOnly={isMobile}
                      label="Ações"
                      options={[
                        {
                          key: "edit",
                          icon: faPenToSquare,
                          label: "Editar desafio",
                          sub: "Ajustar metas / datas",
                          hidden: !c.isCreator,
                          onClick: () => openEditChallenge(c),
                        },
                        {
                          key: "manage",
                          icon: faChartLine,
                          // "entre amigos": todo participante acompanha desempenho e fotos
                          label: me?.role === "aluno" || !c.isCreator ? "Desempenho / fotos" : "Desempenho / inscrever",
                          sub: me?.role === "aluno" || !c.isCreator ? "Adesão, conclusão e fotos de check-in" : "Adesão, conclusão e inscrever alunos",
                          hidden: !(c.isCreator || (c.is_private_invite && c.isParticipant)),
                          onClick: () => setManageChallenge(c),
                        },
                        {
                          // Resultado final: só criador/admin, depois do último dia
                          // (mesma regra do selo "Encerrado": o dia final conta inteiro)
                          key: "results",
                          icon: faTrophy,
                          label: "Anunciar resultado",
                          sub: "Colocação e vencedor por notificação e email",
                          hidden: !(c.isCreator || me?.role === "admin") || challengeTemporalStatus(c) !== "ended",
                          onClick: () => setResultsChallenge(c),
                        },
                        {
                          key: "delete",
                          icon: faTrashCan,
                          label: "Deletar desafio",
                          variant: "danger",
                          hidden: !c.isCreator,
                          onClick: () =>
                            setConfirmDialog({
                              title: "Deletar desafio",
                              message: "Deseja realmente deletar este desafio? Essa ação não pode ser desfeita.",
                              confirmLabel: "Deletar desafio",
                              onConfirm: () => deleteChallenge(challengeId),
                            }),
                        },
                      ]}
                    />
                  )}
                </div>
              </div>

              {isMobile && (
                <small className={styles.cardMetaBlock}>
                  Meta: {c.goal_points} {goalUnit(c.goal_type)} • {
                    temporal === "upcoming"
                      ? `${diasParaIniciar} ${diasParaIniciar === 1 ? "dia para iniciar" : "dias para iniciar"}`
                      : diasRestantes > 0
                      ? `${diasRestantes} ${diasRestantes === 1 ? "dia restante" : "dias restantes"}`
                      : "Encerrado"
                  }
                </small>
              )}

              {/* Descrição completa (regras) só faz sentido pra quem ainda vai
                  decidir se entra. Quem já participa (inclusive o próprio
                  criador, que é auto-inscrito no desafio dele) já sabe as
                  regras — mostrar o texto inteiro de novo a cada abertura do
                  card só empurra progresso/recompensa pra baixo da dobra.
                  Vale tanto no mobile quanto no desktop: sem a descrição,
                  quem ainda não entrou não tem como saber as regras antes
                  de apertar "Participar". */}
              {c.description && !c.isParticipant && (
                <p className={styles.cardDescription}>{c.description}</p>
              )}

              {!isMobile && (
                <div className={styles.challengeHighlights}>
                  <div className={styles.challengeHighlight}>
                    <span className={styles.challengeHighlightLabel}>Meta</span>
                    <strong>{c.goal_points} {goalUnit(c.goal_type)}</strong>
                  </div>
                  <div className={styles.challengeHighlight}>
                    <span className={styles.challengeHighlightLabel}>{temporal === "upcoming" ? "Início em" : "Dias restantes"}</span>
                    <strong>{
                      temporal === "upcoming"
                        ? `${diasParaIniciar} ${diasParaIniciar === 1 ? "dia" : "dias"}`
                        : diasRestantes > 0
                        ? `${diasRestantes} ${diasRestantes === 1 ? "dia" : "dias"}`
                        : "Encerrado"
                    }</strong>
                  </div>
                  <div className={styles.challengeHighlight}>
                    <span className={styles.challengeHighlightLabel}>Recompensa</span>
                    <strong>{c.reward || "Sem recompensa definida"}</strong>
                  </div>
                </div>
              )}

              <div className={styles.cardDates}>
                Início: {formatDateBR(c.start_date)} • Fim: {formatDateBR(c.end_date)}
              </div>

              {c.isParticipant && (
                <>
                  <div className={styles.progressBlock}>
                    <div className={styles.progressHeader}>
                      {!isMobile && <span>Progresso</span>}
                      <strong>{Math.round(progressPercent)}%</strong>
                    </div>
                    <ProgressBar percent={progressPercent} variant="points" />
                  <small className={styles.cardMeta}>
                    {isMobile
                      ? `${c.progress_points}/${c.goal_points} ${goalUnit(c.goal_type)} • ${Math.round(calcProgress(c))}%`
                      : `${c.progress_points}/${c.goal_points} ${goalUnit(c.goal_type)} • ${Math.round(calcProgress(c))}% concluído`}
                  </small>
                  </div>

                  {/* Recompensa (Fase 4 — texto/promessa). Concluído: destaque com
                      CTA de resgate. Em andamento: dica sutil de motivação. */}
                  {c.reward && c.completed && (
                    <div
                      style={{
                        marginTop: 10,
                        padding: "10px 12px",
                        borderRadius: 10,
                        background: "rgba(29,158,117,0.12)",
                        border: "1px solid rgba(29,158,117,0.30)",
                        color: "var(--text)",
                        fontSize: 13,
                        lineHeight: 1.4,
                      }}
                    >
                      🎁 <strong>Recompensa desbloqueada:</strong> {c.reward}
                      <div style={{ color: "var(--muted)", fontSize: 12, marginTop: 2 }}>
                        Combine o resgate com seu professor.
                      </div>
                    </div>
                  )}
                  {c.reward && !c.completed && (
                    <small style={{ display: "block", marginTop: 6, color: "var(--muted)", fontSize: 12 }}>
                      🎁 Recompensa ao concluir: {c.reward}
                    </small>
                  )}

                  {!isMobile && (() => {
                    const timePercent = calcDateProgress(c.start_date, c.end_date);
                    const totalDays = daysBetweenInclusive(c.start_date, c.end_date);
                    const currentDay = dayIndexInclusive(c.start_date);

                    return (
                      <div className={styles.cardTimeBox}>
                        <ProgressBar percent={timePercent} variant="time" />
                        <small className={styles.cardMetaBlock}>
                          Tempo do desafio: {Math.round(timePercent)}% • Dia {Math.min(currentDay, totalDays)}/{totalDays}
                        </small>
                      </div>
                    );
                  })()}
                </>
              )}

              {c.isCreator && c.is_public && (
                <div className={styles.publicLinkBox}>
                  <FontAwesomeIcon icon={faLink} className={styles.publicLinkIcon} />
                  <span className={styles.publicLinkText}>{publicChallengeLink(challengeId)}</span>
                  <button
                    type="button"
                    className={styles.publicLinkCopyBtn}
                    onClick={() => copyPublicChallengeLink(challengeId)}
                  >
                    Copiar
                  </button>
                </div>
              )}

              {!c.isParticipant && !c.isCreator && (
                <div className={styles.cardActions}>
                  <button
                    className={cn(styles.btn, styles.btnPrimary, styles.btnFull)}
                    onClick={() => joinChallenge(challengeId)}
                  >
                    {isMobile ? "Participar" : "Participar do desafio"}
                  </button>
                </div>
              )}
            </Card>
          );
        })}
      </div>
        </Modal>
      )}

      {activeTopTab === 'overview' && (
      <section className={styles.sectionHeading}>
        <div>
          <h2 className={styles.sectionTitle}>Resumo recente</h2>
        </div>
      </section>
      )}

      {activeTopTab === 'overview' && me?.role === "aluno" && (() => {
        // Último treino com sessão: atalho pra rever o resumo depois de fechar
        const last = [...(checkins || [])]
          .filter((c) => c.workout_session_id)
          .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
        if (!last) return null;
        const title = (last.snapshot_workout_plan_title || last.workout_plan_title || last.workout_plan?.title || "Treino").replace(/[—–]/g, "-");
        const when = new Date(last.date).toLocaleDateString("pt-BR", { day: "numeric", month: "long" });
        const mins = Number(last.duration) > 0 ? ` · ${Math.round(Number(last.duration))} min` : "";
        return (
          <SessionSummaryButton
            variant="card"
            sessionId={last.workout_session_id}
            title={title}
            subtitle={`${when}${mins}`}
          />
        );
      })()}

      {activeTopTab === 'overview' && (
      <Card>
        <CheckinsTimeline
          items={checkins}
          exercises={exercises}
          assignedWorkouts={assignedWorkouts}
          userId={user?.id}
          canEditLabel={user?.role === "professor" || user?.role === "admin"}
          onSessionClick={(sessionId, checkin) => setSessionDetail({ sessionId, checkin })}
          onDeleteCheckin={async (checkinId, uid) => {
            try {
              await api.delete(`/users/${uid}/checkins/${checkinId}`);
              await loadData();
            } catch (err) {
              const msg = err?.response?.data?.detail || err?.message || 'Erro ao excluir check-in';
              setToast({ type: 'error', message: msg });
              throw err; // re-throw so the button shows error state
            }
          }}
        />
      </Card>
      )}

      {/* Menu mobile — mesma lista do desktop (menuOptions). Antes eram ~19
          <button> escritos a mao aqui, uma segunda implementacao que ja tinha
          divergido dos gates de plano do desktop. O botao "hamburguer" do
          header continua sendo o dono da abertura, por isso o ActionMenu vem
          controlado e com o gatilho interno suprimido. */}
      {isMobile && (
        <ActionMenu
          isMobile
          roleUser={me?.role}
          label="Menu"
          options={menuOptions}
          open={showMenu}
          onOpenChange={setShowMenu}
          hideTrigger
        />
      )}

            {showProfileModal && (
        <ProfileModal
          open={showProfileModal}
          onClose={() => {setShowProfileModal(false); setShowMenu(false);}}
          user={me}
          isPlus={me?.role === "aluno" && me?.is_self_service && hasPaidAccess}
          onUpdated={(nextMe) => {
            setMe(nextMe);
            setTotalPoints(nextMe.points);
            // Sincroniza o user do App (localStorage["user"]) — sem isso, um refresh
            // reaplica a cor/dados antigos do login por cima dos salvos no perfil
            onUserUpdate?.(nextMe);
          }}
        />
      )}

      {openUsersModal && (
      <UserAdminModal
        open={openUsersModal}
        onClose={() => setOpenUsersModal(false)}
        apiBase={apiUrl}
        token={localStorage.getItem("token")}
        onUpdated={(nextMe) => {
          setMe(nextMe);
          setTotalPoints(nextMe.points);
          onUserUpdate?.(nextMe);
        }}
      />
      )}

      <AiUsageModal
        open={showAiUsageModal}
        onClose={() => setShowAiUsageModal(false)}
      />

      <SendEmailModal
        open={showSendEmailModal}
        onClose={() => setShowSendEmailModal(false)}
      />

      <AdminSendPushModal
        open={showAdminPushModal}
        onClose={() => setShowAdminPushModal(false)}
      />

      <ChallengeResultsModal
        open={!!resultsChallenge}
        challenge={resultsChallenge}
        onClose={() => setResultsChallenge(null)}
      />

      <FriendChallengeModal
        open={showFriendChallengeModal}
        onClose={() => setShowFriendChallengeModal(false)}
        userId={user?.id}
        creatorName={me?.name}
        onCreated={() => loadData()}
      />

      <ChallengeManageModal
        open={!!manageChallenge}
        challenge={manageChallenge}
        onClose={() => setManageChallenge(null)}
        viewerRole={me?.role}
      />

      <CardioCheckinModal
        open={showCardioModal}
        onClose={() => {
          setShowCardioModal(false);
          if (cardioSavedRef.current) {
            cardioSavedRef.current = false;
            maybeNudgeInstallAfterWorkout();
          }
        }}
        onSuccess={(hadLocation) => {
          loadData();
          cardioSavedRef.current = true;
          if (hadLocation) {
            cardioLabelTimersRef.current.forEach(clearTimeout);
            cardioLabelTimersRef.current = [4000, 8000, 14000, 22000, 32000, 45000].map((delay) =>
              setTimeout(() => { loadData(); }, delay)
            );
          }
        }}
        photoRequired={photoRequiredNow}
      />

      <PaymentsModal
        open={showPaymentsModal}
        onClose={() => setShowPaymentsModal(false)}
        currentUser={me}
        onPaymentConfirmed={handlePaymentConfirmed}
      />

      <PaymentsAdminModal
        open={showPaymentsAdminModal}
        onClose={() => setShowPaymentsAdminModal(false)}
      />

      {me?.organization_id && me?.org_role && (
        <OrganizationModal
          open={showOrganization}
          onClose={() => setShowOrganization(false)}
          me={me}
          apiUrl={apiUrl}
          onOpenPayments={() => { setShowOrganization(false); setTimeout(() => setShowPaymentsModal(true), 200); }}
        />
      )}

      <ProfessorBillingModal
        open={showProfessorBilling}
        refetchBillingAccess={refetchProfessorBillingAccess}
        onClose={() => setShowProfessorBilling(false)}
        currentUser={me}
        onOpenCheckout={handleOpenProfessorCheckout}
        hasStudentBillingManagementAccess={hasStudentBillingManagementAccess}
        hasActivePlatformSubscription={hasActivePlatformSubscription}
        lastSubscription={lastSubscription}
        lastOneOffAccess={lastOneOffAccess}
      />

      <ProfessorCheckoutModal
        open={showProfessorCheckout}
        onClose={() => setShowProfessorCheckout(false)}
        currentUser={me}
        onPaymentConfirmed={async () => {
          await refetchProfessorBillingAccess?.();
        }}
        planId={selectedProfessorPlan?.id}
        planName={selectedProfessorPlan?.name}
        planAmount={selectedProfessorPlan?.amount}
      />

      {me?.role === "admin" && (
        <OrganizationsAdminModal
          open={showOrganizationsAdmin}
          onClose={() => setShowOrganizationsAdmin(false)}
          onNewOrganization={() => { setInviteProfessorKind("academy"); setShowInviteProfessor(true); }}
          reloadKey={orgsReloadKey}
        />
      )}

      {/* Depois da lista de academias de propósito: mesmo z-index, então o
          convite aberto por "Nova academia" fica por cima da lista. */}
      <InviteProfessorModal
        open={showInviteProfessor}
        initialKind={inviteProfessorKind}
        onClose={() => { setShowInviteProfessor(false); setOrgsReloadKey((k) => k + 1); }}
      />

      <SelfInsightsModal
        open={openSelfInsights}
        onClose={() => setOpenSelfInsights(false)}
        user={me}
      />

      {openWorkoutAdmin && (
      <WorkoutAdminModal
        open={openWorkoutAdmin}
        onClose={() => setOpenWorkoutAdmin(false)}
        apiBase={apiUrl}
        token={localStorage.getItem("token")}
        isMobile={isMobile}
      />
      )}

      {openBodyMeasurements && (
        <BodyMeasurementsModal
          open={openBodyMeasurements}
          onClose={() => setOpenBodyMeasurements(false)}
          apiUrl={apiUrl}
          currentUser={me}
        />
      )}


      <NutritionModal
        open={openNutrition}
        onClose={() => { setOpenNutrition(false); refetchHasDiet(); }}
        currentUser={me}
        canCreateOwnDiet={me?.role === "aluno" && me?.is_self_service && !me?.professor_id && hasPaidAccess}
      />

      <ChatModal
        open={chatEntry.open}
        onClose={chatEntry.handleClose}
        roomId={chatEntry.roomId}
        currentUserId={me?.id}
        showTabs={chatEntry.showTabs}
        activeTab={chatEntry.activeTab}
        onTabChange={chatEntry.onTabChange}
        status={chatEntry.chat.status}
        messages={chatEntry.chat.messages}
        hasMoreHistory={chatEntry.chat.hasMoreHistory}
        loadingHistory={chatEntry.chat.loadingHistory}
        typingUsers={chatEntry.chat.typingUsers}
        loadOlderMessages={chatEntry.chat.loadOlderMessages}
        sendMessage={chatEntry.chat.sendMessage}
        sendTyping={chatEntry.chat.sendTyping}
      />

      {/* ── Rest timer banner fixo no topo ── */}
      {activeRest && (
        <RestBanner
          key={activeRest.itemId}
          seconds={activeRest.seconds}
          exerciseName={activeRest.exerciseName}
          onDone={() => setActiveRest(null)}
        />
      )}

      <WorkoutSummaryModal
        summary={delayedSummary}
        onClose={() => {
          // Desafio que não contou este treino: avisa DEPOIS do resumo (fora do
          // print que o aluno posta), num toast que some sozinho.
          (delayedSummary?.challengeNotes || []).forEach((note) => {
            toastApi.warning(`Não contou para "${note.challenge_title}": ${(note.reasons || []).join(" · ")}`, 8000);
          });
          setWorkoutSummary(null); setDelayedSummary(null); maybeNudgeInstallAfterWorkout();
          // Só no resumo do fim do treino (não conta quando o aluno reabre depois)
          toastApi.info?.("Você pode rever este resumo depois em Último treino ou no histórico.", 6000);
        }}
      />


      <SessionDetailModal
        sessionId={sessionDetail?.sessionId}
        checkin={sessionDetail?.checkin}
        onClose={() => setSessionDetail(null)}
      />

      <RestConflictModal
        open={!!restConflict}
        message={restConflict?.message}
        conflicts={restConflict?.conflicts || []}
        saving={restConflictSaving}
        onConfirm={confirmRestConflict}
        onCancel={cancelRestConflict}
      />

      {/* ── Confirmar Cancelamento de Sessão ── */}
      {showCancelSessionConfirm && (
        <div className={styles.confirmOverlay} onClick={() => setShowCancelSessionConfirm(false)}>
          <div className={styles.confirmBox + ' ' + styles.confirmBoxDanger} onClick={e => e.stopPropagation()}>
            <div className={styles.confirmIconLarge}><FontAwesomeIcon icon={faTriangleExclamation} /></div>
            <h3 className={styles.confirmTitle} style={{color:'#f87171'}}>Cancelar treino?</h3>
            <div className={styles.confirmWarnBanner + ' ' + styles.confirmWarnBannerDanger}>
              Esta ação <strong>não pode ser desfeita.</strong>
            </div>
            <p className={styles.confirmMsg}>
              Todo o progresso desta sessão será <strong>apagado permanentemente</strong> —
              nenhuma série, nenhum exercício e nenhum ponto ficam salvos. É como se o
              treino nunca tivesse começado.
            </p>
            <div className={styles.confirmActions}>
              <button className={styles.confirmBtnSecondary} onClick={() => setShowCancelSessionConfirm(false)}>Voltar</button>
              <button className={styles.confirmBtnDanger} onClick={handleCancelSession}>Sim, cancelar tudo</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirmar Finalização Antecipada ── */}
      {showFinishNowConfirm && (() => {
        const _setsLog = workoutSession?.setsLog || {};
        const _hasValidWeight = Object.values(_setsLog).some(sets =>
          (sets || []).some(s => s.done && parseFloat(s.weight || 0) > 0 && parseFloat(s.reps || 0) > 0)
        );
        return (
          <div className={styles.confirmOverlay} onClick={() => setShowFinishNowConfirm(false)}>
            <div className={styles.confirmBox} onClick={e => e.stopPropagation()}>
              <div className={styles.confirmIconLarge}><FontAwesomeIcon icon={faFlagCheckered} /></div>
              <h3 className={styles.confirmTitle}>Finalizar antes da hora?</h3>
              <div className={styles.confirmWarnBanner + ' ' + styles.confirmWarnBannerWarning}>
                Esta ação <strong>não pode ser desfeita.</strong>
              </div>
              <p className={styles.confirmMsg}>
                Isso <strong>não é a forma correta de completar</strong> o treino. Para completar
                direito, preencha as séries de cada exercício conforme for executando.
              </p>
              <p className={styles.confirmMsg}>
                Se você finalizar agora, os exercícios que ainda faltam preencher são
                registrados automaticamente com <strong>0 repetições e 0 peso</strong>. Só o que
                você já preencheu até aqui é salvo.
              </p>
              {!_hasValidWeight && (
                <div className={styles.confirmWarnBanner + ' ' + styles.confirmWarnBannerDanger}>
                  <FontAwesomeIcon icon={faChartBar} style={{marginRight: 6}} />
                  <span>Nenhuma série com peso foi registrada — este treino <strong>não entrará nos gráficos</strong> nem na contagem de treinos válidos.</span>
                </div>
              )}
              <div className={styles.confirmActions}>
                <button className={styles.confirmBtnSecondary} onClick={() => setShowFinishNowConfirm(false)} disabled={finishNowLoading}>Voltar</button>
                <button className={styles.confirmBtnSuccess} onClick={handleFinishNow} disabled={finishNowLoading}>
                  {finishNowLoading ? 'Finalizando...' : 'Finalizar mesmo assim'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {showFeedbackModal && (
        <FeedbackInboxModal
          open={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          feedbacks={unreadFeedbacks}
          apiUrl={apiUrl}
          onMarkRead={(ids) => {
            api.post("/session-feedback/mark-read", ids).catch(() => {});
            setUnreadFeedbacks(prev => prev.filter(f => !ids.includes(f.id)));
          }}
        />
      )}

      {showCreateModal && (
        <Modal
          onClose={() => {
            setShowCreateModal(false);
            setSelectedChallenge(null);
          }}
          onConfirm={createChallenge}
          confirmLabel={selectedChallenge ? "Salvar alterações" : "Criar desafio"}
          isMobile={isMobile}
        >
          <div className={styles.modalIntro}>
            <h2 className={styles.h2}>{selectedChallenge ? "Editar desafio" : "Criar novo desafio"}</h2>
            <p className={styles.muted}>Preencha as informações abaixo</p>
          </div>

          <div className={styles.formGrid}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="challenge-title">Título do desafio</label>
              <input id="challenge-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={styles.input} />
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="challenge-description">Descrição</label>
              <textarea
                id="challenge-description"
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className={styles.textarea}
              />
            </div>

            <div className={styles.twoCols}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="challenge-goal-type">Tipo de meta</label>
                <select
                  id="challenge-goal-type"
                  value={form.goal_type}
                  onChange={(e) => setForm({ ...form, goal_type: e.target.value })}
                  className={styles.input}
                >
                  {CHALLENGE_GOAL_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.optLabel}</option>
                  ))}
                </select>
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="challenge-category-filter">Conta o quê?</label>
                <select
                  id="challenge-category-filter"
                  value={form.category_filter}
                  onChange={(e) => setForm({ ...form, category_filter: e.target.value })}
                  className={styles.input}
                >
                  <option value="">Todos os treinos</option>
                  <option value="cardio">Só cardio</option>
                  <option value="musculacao">Só musculação</option>
                </select>
              </div>
            </div>

            <div className={styles.twoCols}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="challenge-min-duration">Duração mínima (min, opcional)</label>
                <input
                  id="challenge-min-duration"
                  type="number"
                  min="0"
                  placeholder="Sem mínimo"
                  value={form.min_duration_minutes}
                  onChange={(e) => setForm({ ...form, min_duration_minutes: e.target.value })}
                  className={styles.input}
                />
                <small className={styles.helperText}>Check-in com duração menor não conta pra meta.</small>
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="challenge-requires-location">Localização</label>
                <label className={styles.radioLabel} style={{ marginTop: 8 }}>
                  <input
                    id="challenge-requires-location"
                    type="checkbox"
                    checked={!!form.requires_location}
                    onChange={(e) => setForm({ ...form, requires_location: e.target.checked })}
                  />{" "}
                  Exigir geolocalização no check-in
                </label>
                <small className={styles.helperText}>Check-in sem localização registrada não conta pra meta.</small>
              </div>
            </div>

            {form.requires_location && (
              <div className={styles.field}>
                <label className={styles.label}>Ponto de referência (opcional)</label>
                <small className={styles.helperText}>
                  Deixe em branco pra manter o comportamento atual (só exige que o check-in
                  tenha alguma localização registrada, sem comparar com um lugar específico).
                  Preenchendo os três campos, só conta pra meta o check-in feito dentro do raio
                  a partir deste ponto (ex.: a academia do desafio).
                </small>
                <div className={styles.twoCols} style={{ marginTop: 8 }}>
                  <input
                    type="number"
                    step="any"
                    placeholder="Latitude"
                    value={form.location_lat}
                    onChange={(e) => setForm({ ...form, location_lat: e.target.value })}
                    className={styles.input}
                  />
                  <input
                    type="number"
                    step="any"
                    placeholder="Longitude"
                    value={form.location_lon}
                    onChange={(e) => setForm({ ...form, location_lon: e.target.value })}
                    className={styles.input}
                  />
                </div>
                <div className={styles.twoCols} style={{ marginTop: 8 }}>
                  <input
                    type="number"
                    min="0"
                    placeholder="Raio em metros (ex.: 150)"
                    value={form.location_radius_m}
                    onChange={(e) => setForm({ ...form, location_radius_m: e.target.value })}
                    className={styles.input}
                  />
                  <button
                    type="button"
                    className={cn(styles.btn, styles.btnSecondary)}
                    onClick={captureChallengeLocation}
                    disabled={capturingChallengeLocation}
                  >
                    {capturingChallengeLocation ? "Obtendo localização..." : "Usar minha localização atual"}
                  </button>
                </div>
              </div>
            )}

            <div className={styles.field}>
              <label className={styles.label} htmlFor="challenge-goal-points">{goalMeta(form.goal_type).metaLabel}</label>
              <input
                id="challenge-goal-points"
                type="number"
                value={form.goal_points}
                onChange={(e) => setForm({ ...form, goal_points: e.target.value })}
                className={styles.input}
              />
            </div>

            <div className={styles.twoCols}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="challenge-start-date">Data de início</label>
                <SplitDateInput
                  value={form.start_date}
                  onChange={(v) => setForm({ ...form, start_date: v })}
                  inputClassName={styles.input}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="challenge-end-date">Data de término</label>
                <SplitDateInput
                  value={form.end_date}
                  onChange={(v) => setForm({ ...form, end_date: v })}
                  inputClassName={styles.input}
                />
              </div>
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="challenge-reward">Recompensa (opcional)</label>
              <input id="challenge-reward" value={form.reward} onChange={(e) => setForm({ ...form, reward: e.target.value })} className={styles.input} />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Visibilidade</label>
              <div>
                <label className={styles.radioLabel}>
                  <input type="radio" name="is_public" checked={!form.is_public} onChange={() => setForm({ ...form, is_public: false })} />
                  {" "}
                  {me?.role === "admin"
                    ? "Privado (visível a todos os alunos, sem aparecer na vitrine pública)"
                    : "Somente para meus alunos"}
                </label>
                <label className={styles.radioLabel}>
                  <input type="radio" name="is_public" checked={form.is_public} onChange={() => setForm({ ...form, is_public: true })} /> Público
                </label>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {showRankingModal && (() => {
        const myParticipatingChallenges = allChallengesUnified.filter(c => c.isParticipant);
        const hasExtraChallenges = myParticipatingChallenges.length > 0;

        // Escolher dados conforme tab ativa
        const rankData =
          rankingTab === "weekly"   ? weeklyRanking :
          rankingTab === "monthly"  ? monthlyRanking :
          rankingTab === "all"      ? ranking :
          challengeRanking;
        const isLoadingRank =
          (rankingTab === "weekly" || rankingTab === "monthly") ? loadingPeriodRanking :
          rankingTab === "challenge" ? loadingChallengeRanking : false;

        const tabTitles = { weekly: "7 dias", monthly: "30 dias", all: "Geral", challenge: "Desafio" };

        // Entrada do usuário fora do top 10 (destacada logo após o pódio)
        const myOutEntry = !isLoadingRank && rankData.find(r => r.id === user?.id && r.position > 10);
        const mainList = myOutEntry
          ? rankData.filter(r => !(r.id === user?.id && r.position > 10))
          : rankData;

        return (
          <Modal onClose={() => { setShowRankingModal(false); setRankingChallengeId(null); setRankingTab("weekly"); }} confirmLabel="Fechar" onConfirm={() => { setShowRankingModal(false); setRankingChallengeId(null); setRankingTab("weekly"); }} isMobile={isMobile} hideCancelBtn={true}>
            <h2 className={cn(styles.h2, styles.modalTitle)}>
              <span className={styles.rankHeaderIcon}>
                <FontAwesomeIcon icon={faChartLine} />
              </span>
              Ranking
              {(() => {
                const topCount = mainList.length;
                const label = topCount > 10 ? `Top 10 + ${topCount - 10} empate${topCount - 10 > 1 ? 's' : ''}` : `Top ${topCount}`;
                return <span className={styles.rankTopBadge}>{label}{myOutEntry ? ' + sua posição' : ''}</span>;
              })()}
            </h2>

            {/* ── Tabs ── */}
            <div className={styles.rankTabs}>
              {["weekly", "monthly", "all"].map(tab => (
                <button key={tab} className={cn(styles.rankTab, rankingTab === tab && styles.rankTabActive)}
                  onClick={() => setRankingTab(tab)}>
                  {tabTitles[tab]}
                </button>
              ))}
              {hasExtraChallenges && (
                <button className={cn(styles.rankTab, rankingTab === "challenge" && styles.rankTabActive)}
                  onClick={() => { setRankingTab("challenge"); if (!rankingChallengeId) setRankingChallengeId(myParticipatingChallenges[0]?.challenge_id); }}>
                  Por desafio
                </button>
              )}
            </div>

            {/* Seletor de desafio (só quando tab = challenge) */}
            {rankingTab === "challenge" && hasExtraChallenges && (
              <div className={styles.rankingFilter}>
                <select
                  className={styles.rankFilterSelect}
                  value={rankingChallengeId ?? ""}
                  onChange={e => setRankingChallengeId(e.target.value ? Number(e.target.value) : null)}
                >
                  {myParticipatingChallenges.map(c => (
                    <option key={c.challenge_id} value={c.challenge_id}>
                      {c.title}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className={styles.rankList}>
              {isLoadingRank ? (
                <div className={styles.rankSkeletonList}>
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className={styles.rankSkeletonRow}>
                      <div className={cn(styles.skeletonBlock, styles.rankSkeletonPos)} />
                      <div className={cn(styles.skeletonBlock, styles.rankSkeletonAvatar)} />
                      <div className={styles.rankSkeletonLines}>
                        <div className={cn(styles.skeletonBlock, styles.rankSkeletonName)} />
                        <div className={cn(styles.skeletonBlock, styles.rankSkeletonPts)} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : rankData.length === 0 ? (
                <div className={styles.emptyState} style={{ padding: "32px 0", textAlign: "center" }}>
                  <p className={styles.muted}>Nenhum treino registrado{rankingTab === "weekly" ? " nos últimos 7 dias" : rankingTab === "monthly" ? " nos últimos 30 dias" : ""} ainda.</p>
                </div>
              ) : (() => {
                const renderRankRow = (r) => {
                  const isMyStudent = me?.role === "professor" && r?.role === "aluno" && r?.professor_id === me?.id;
                  const medalColor = r.position === 1 ? '#f59e0b' : r.position === 2 ? '#9ca3af' : '#cd7f32';
                  return (
                    <div key={r.id} className={cn(styles.rankRow, r.id === user.id && styles.rankRowMe, isMyStudent && styles.rankRowHasBadge)}>
                      <div className={styles.rankLeft}>
                        <div className={styles.rankPosition}>
                          {r.position <= 3 ? (
                            <FontAwesomeIcon icon={r.position === 1 ? faTrophy : faMedal} style={{ color: medalColor, fontSize: 18 }} />
                          ) : (
                            <strong className={styles.rankPos}>{r.position}º</strong>
                          )}
                        </div>
                        <span className={styles.rankAvatarWrap}>
                          <img src={resolveAvatarUrl(r.avatar_url, r.name)} className={styles.rankAvatar} alt={r.name} loading="lazy" referrerPolicy="no-referrer" />
                          {r.is_plus && <PlusBadge size={14} title={`${r.name} é assinante Plus`} />}
                        </span>
                        <span className={styles.rankName}>
                          <span className={styles.rankNameText} title={r.name}>
                            {(() => { const p = (r.name || '').trim().split(/\s+/); return p.length > 1 ? `${p[0]} ${p[p.length - 1]}` : p[0]; })()}
                          </span>
                          {r.completed && <span className={styles.rankBadgeCompleted}><FontAwesomeIcon icon={faCheck} /> Concluído</span>}
                        </span>
                      </div>
                      <div className={styles.rankRightCol}>
                        <strong className={styles.rankPoints}>{r.points} pts</strong>
                        {isMyStudent && <span className={styles.rankBadgeMyStudent}>Meu aluno</span>}
                      </div>
                    </div>
                  );
                };
                return (
                  <>
                    {mainList.slice(0, 3).map(renderRankRow)}
                    {myOutEntry && (() => {
                      const gapPts = mainList.length >= 10
                        ? Math.max(0, (mainList[9]?.points ?? 0) - (myOutEntry.points ?? 0))
                        : null;
                      const zeroLabel =
                        rankingTab === "weekly"   ? "Você ainda não pontuou nos últimos 7 dias" :
                        rankingTab === "monthly"  ? "Você ainda não pontuou nos últimos 30 dias" :
                        "Você ainda não tem pontos registrados";
                      return (
                        <>
                          <div className={styles.rankSeparator}><span>- Sua posição -</span></div>
                          {myOutEntry.points === 0 ? (
                            <div className={styles.rankNudge}>
                              <FontAwesomeIcon icon={faDumbbell} className={styles.rankNudgeIcon} />
                              <span>{zeroLabel} - treine hoje para entrar no ranking!</span>
                            </div>
                          ) : (
                            <>
                              {renderRankRow(myOutEntry)}
                              {gapPts > 0 && (
                                <div className={styles.rankGapChip}>
                                  ↑ Faltam <strong>{gapPts} pts</strong> para entrar no top 10
                                </div>
                              )}
                            </>
                          )}
                        </>
                      );
                    })()}
                    {mainList.slice(3).map(renderRankRow)}
                  </>
                );
              })()}
            </div>
          </Modal>
        );
      })()}

      {showPointsModal && (
        <Modal
          onClose={() => setShowPointsModal(false)}
          onConfirm={() => setShowPointsModal(false)}
          confirmLabel="Fechar"
          hideCancelBtn={true}
          isMobile={isMobile}
          title="Seus Pontos"
        >
          <div className={styles.grid} style={{ gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr' }}>
            <div className={styles.card}>
              <div className={styles.cardTitle}>Resumo</div>
              <div className={styles.heroMeta} style={{ marginTop: 8 }}>
                <span className={styles.heroMetaPill}>Total: {totalPoints} pts</span>
                <span className={styles.heroMetaPill}>Hoje: +{pointsToday}</span>
              </div>
            </div>

            <div className={styles.card}>
              <div className={styles.cardTitle}>Últimos 7 dias</div>
              <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
                {(() => {
                  const map = new Map(pointsHistory.map(p => [String(p.day).slice(0,10), Number(p.points || 0)]));
                  const out = [];
                  const now = new Date();
                  let max = 1;
                  for (let i = 6; i >= 0; i--) {
                    const d = new Date(now);
                    d.setDate(now.getDate() - i);
                    const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
                    const pts = map.get(key) || 0;
                    max = Math.max(max, pts);
                    out.push({ key, date: new Date(d), points: pts });
                  }
                  return out.map((row) => {
                    const label = new Intl.DateTimeFormat('pt-BR', { weekday: 'short', day: '2-digit' }).format(row.date);
                    const pct = max > 0 ? Math.round((row.points / max) * 100) : 0;
                    return (
                      <div key={row.key} style={{ display: 'grid', gridTemplateColumns: '90px 1fr auto', alignItems: 'center', gap: 8 }}>
                        <span className={styles.muted} style={{ textTransform: 'capitalize' }}>{label}</span>
                        <div className={styles.barTrack}>
                          <div className={cn(styles.barFill, styles.barPoints)} style={{ width: `${pct}%` }} />
                        </div>
                        <strong style={{ minWidth: 52, textAlign: 'right' }}>{row.points} pts</strong>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {showCheckinModal && (
        <Modal
          onConfirm={() => {
            if (photoRequiredNow && !form.photo) {
              setToast({ type: "info", message: "Anexe uma foto: você participa de um desafio que exige registro fotográfico." });
              return;
            }
            const allDone = workoutSession && allExercisesCompleted(selectedWorkoutAssignment);
            const hasManualTime = !allDone && form.duration && Number(form.duration) > 0;
            openConfirm({
              title: hasManualTime ? "⚠️ Registrar com exercícios pendentes?" : "Confirmar registro?",
              message: hasManualTime
                ? `Registrará ${form.duration} minutos.\n\nOs exercícios NÃO FINALIZADOS serão zerados — apenas o tempo será registrado.\n\nO caminho ideal é preencher reps e peso de cada exercício até finalizar toda a ficha: assim o treino se encerra automaticamente e o tempo real é calculado.`
                : "Confirma o registro deste treino?",
              confirmLabel: "Registrar",
              confirmVariant: "success",
              onConfirm: confirmCheckin,
            });
          }}
          onClose={() => { setShowCheckinModal(false); setWorkoutModalPreStart(false); setWorkoutModalReadonly(false); }}
          confirmLabel={(() => {
            if (checkingIn) return "Registrando...";
            if (!selectedCheckinTarget) return "Selecione um treino para continuar";
            const allDone = workoutSession && allExercisesCompleted(selectedWorkoutAssignment);
            const minutes = allDone ? Math.round(elapsedSeconds / 60) : (form.duration || 0);
            // Achado UX #3 (16/09/2026): quando o tempo vem do campo manual (via
            // de finalização antecipada, com exercícios pendentes), o rótulo do
            // botão usava o mesmo texto do caminho feliz ("Registrar treino"),
            // mesmo esse número não tendo nenhuma relação com o cronômetro real
            // (timerBadge) exibido no topo — os dois pareciam se contradizer.
            // Diferenciar aqui deixa claro, antes do clique, que é o valor
            // digitado, não o tempo decorrido da sessão.
            const hasManualTime = !allDone && form.duration && Number(form.duration) > 0;
            return hasManualTime
              ? `Registrar com tempo manual • ${minutes} min`
              : `Registrar treino • ${minutes} min`;
          })()}
          confirmDisabled={(() => {
            if (checkingIn) return true;
            if (!selectedWorkoutAssignment) return true;
            if (workoutModalReadonly || availableCheckinsToday <= 0) return true;
            const allDone = workoutSession && allExercisesCompleted(selectedWorkoutAssignment);
            const hasMinutes = allDone || (!!form.duration && Number(form.duration) > 0);
            if (photoRequiredNow && !form.photo) return true;
            return !hasMinutes;
          })()}
          isMobile={isMobile}
          hideCancelBtn={true}
        >
          {checkinError && (
            <div className={styles.checkinErrorInline}>
              <span>{checkinError}</span>
              <button
                type="button"
                className={cn(styles.btn, styles.btnSecondary)}
                onClick={() => {
                  const allDone = workoutSession && allExercisesCompleted(selectedWorkoutAssignment);
                  const hasManualTime = !allDone && form.duration && Number(form.duration) > 0;
                  openConfirm({
                    title: hasManualTime ? "⚠️ Registrar com exercícios pendentes?" : "Confirmar registro?",
                    message: hasManualTime
                      ? `Registrará ${form.duration} minutos.\n\nOs exercícios NÃO FINALIZADOS serão zerados — apenas o tempo será registrado.\n\nO caminho ideal é preencher reps e peso de cada exercício até finalizar toda a ficha: assim o treino se encerra automaticamente e o tempo real é calculado.`
                      : "Confirma o registro deste treino?",
                    confirmLabel: "Registrar",
                    confirmVariant: "success",
                    onConfirm: confirmCheckin,
                  });
                }}
                disabled={checkingIn}
              >
                {checkingIn ? "Tentando..." : "Tentar novamente"}
              </button>
            </div>
          )}

            <>
              {selectedWorkoutAssignment && (
                <div className={styles.workoutHeaderBlock}>
                  <span className={styles.workoutHeaderName}>
                    {(selectedWorkoutAssignment.custom_name || selectedWorkoutAssignment.workout_plan?.title || `Treino #${selectedWorkoutAssignment.workout_plan_id}`).replace(/[—–]/g, '-')}
                  </span>
                  {workoutSession && (
                    <div className={styles.workoutHeaderActions}>
                      {!workoutModalReadonly && (
                        <>
                          <button
                            type="button"
                            className={styles.btnCancelSession}
                            onClick={() => setShowCancelSessionConfirm(true)}
                            title="Cancelar treino sem salvar"
                          >
                            Cancelar
                          </button>
                          <button
                            type="button"
                            className={styles.btnFinishNow}
                            onClick={() => setShowFinishNowConfirm(true)}
                            title="Finalizar antes da hora — não é o mesmo que completar o treino; o que faltar vira 0"
                          >
                            Finalizar
                          </button>
                        </>
                      )}
                      <div className={styles.timerBadge} aria-label="Tempo decorrido do treino">
                        <FontAwesomeIcon icon={faClock} />
                        <strong className={styles.timerStrong}>{formatElapsed(elapsedSeconds)}</strong>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {selectedWorkoutAssignment && (
                <div className={styles.field}>

                  <div className={styles.workoutSummaryGrid}>
                    {(() => {
                      const itemsOrdered = (selectedWorkoutAssignment.items || []).slice().sort((a, b) => Number(a.order_index || 0) - Number(b.order_index || 0));
                      const blocks = buildRenderBlocks(itemsOrdered);
                      const totalExercises = itemsOrdered.length;
                      const completedSoFar = Object.keys(workoutSession?.exerciseCompletions || {}).length;
                      const customColor = me?.professor_brand_color ?? me?.own_brand_color;

                      // Bi-set/superset/circuito: qual BLOCO (item avulso ou
                      // grupo inteiro) está em andamento agora — generaliza a
                      // antiga activeExerciseId (que só olhava 1 item) pra
                      // travar/destravar por bloco. Um grupo conta como "em
                      // andamento" assim que QUALQUER item dele tem série
                      // iniciada — os outros itens do MESMO grupo nunca ficam
                      // travados entre si (é o objetivo do bi-set).
                      const activeBlockKey = (() => {
                        if (!workoutSession) return null;
                        const setsLog = workoutSession.setsLog || {};
                        const completions = workoutSession.exerciseCompletions || {};
                        for (const block of blocks) {
                          const blockItems = block.kind === "group" ? block.items : [block.item];
                          const allDone = blockItems.every((it) => !!completions[it.id]);
                          if (allDone) continue;
                          const anyStarted = blockItems.some((it) => {
                            const sets = setsLog[it.id];
                            return Array.isArray(sets) && sets.some((s) => s.done);
                          });
                          if (anyStarted) return blockKeyOf(block);
                        }
                        return null;
                      })();

                      return blocks.map((block) => {
                        const blockItems = block.kind === "group" ? block.items : [block.item];
                        const blockKey = blockKeyOf(block);
                        const isDone = blockItems.every((it) => !!(workoutSession?.exerciseCompletions && workoutSession.exerciseCompletions[it.id]));
                        const isRestingThis = block.kind === "group"
                          ? (Array.isArray(activeRest?.itemIds) && blockItems.some((it) => activeRest.itemIds.includes(it.id)))
                          : (activeRest?.itemId === block.item.id);
                        const isLocked = !!workoutSession && !workoutModalReadonly && !isDone && (
                          (activeBlockKey !== null && activeBlockKey !== blockKey) ||
                          (activeRest !== null && !isRestingThis)
                        );

                        // ── Bloco avulso (item único) — comportamento inalterado ──
                        if (block.kind === "item") {
                          const item = block.item;
                          const index = itemsOrdered.findIndex((x) => x.id === item.id);
                          const summary = [
                            item.sets_planned ? `${item.sets_planned}x` : null,
                            item.reps_planned || null,
                            item.target_rir != null ? `deixe ${item.target_rir} reps na reserva` : null,
                            item.duration_planned ? `${item.duration_planned} min` : null,
                            item.weight_planned ? `${item.weight_planned} kg` : null,
                          ]
                            .filter(Boolean)
                            .join(" • ");

                          if (isDone && !isRestingThis) {
                            const completedSets = (workoutSession?.setsLog?.[item.id] || []).filter(s => s.done).length;
                            return (
                              <div
                                key={blockKey}
                                className={styles.workoutSummaryCardCollapsed}
                                role="button"
                                aria-label={`${item.exercise_item?.name || `Item ${item.id}`} — concluído. Toque para ver demonstração`}
                                onClick={() => item.exercise_item && setDemoExerciseItem(item.exercise_item)}
                              >
                                <div className={styles.collapsedRow}>
                                  <FontAwesomeIcon icon={faCheckCircle} className={styles.doneIcon} />
                                  <span className={styles.collapsedName}>{item.exercise_item?.name || `Item ${item.id}`}</span>
                                  <span className={styles.collapsedMeta}>
                                    {completedSets > 0 ? `${completedSets} séries` : "concluído"}
                                  </span>
                                </div>
                              </div>
                            );
                          }

                          return (
                            <div
                              key={blockKey}
                              className={cn(styles.workoutSummaryCard, isLocked && styles.workoutSummaryCardLocked)}
                              role={isLocked ? undefined : "button"}
                              title={undefined}
                              onClick={isLocked ? undefined : (() => item.exercise_item && setDemoExerciseItem(item.exercise_item))}
                            >
                              <div className={styles.workoutSummaryTop}>
                                {/* Achado UX #1 (16/09/2026): antes mostrava um check
                                    verde aqui mesmo com 0/N séries feitas — sugeria
                                    "concluído" num exercício que nem começou. O check
                                    real de concluído já existe no card colapsado
                                    (workoutSummaryCardCollapsed) logo abaixo, quando
                                    isDone é true; aqui é só o rótulo do exercício em
                                    andamento, sem ícone de status. */}
                                <div className={styles.workoutSummaryLabel}>
                                  Exercício {index + 1}
                                </div>
                                {item.exercise_item && (
                                  <button
                                    type="button"
                                    className={styles.demoBtnPulse}
                                    onClick={(e) => { e.stopPropagation(); setDemoExerciseItem(item.exercise_item); }}
                                    aria-label="Ver demonstração do exercício"
                                  >
                                    <FontAwesomeIcon icon={faPlay} />
                                    Demo
                                  </button>
                                )}
                              </div>
                              <div className={styles.workoutSummaryTitle}>
                                <span>{item.exercise_item?.name || `Item ${item.id}`}</span>
                              </div>
                              {summary && <div className={styles.workoutSummaryNote}>{summary}</div>}

                              {/* Achado UX #5 (16/09/2026): as instruções de execução (texto
                                  longo, 2-3 linhas) ficavam entre o resumo e os campos de
                                  reps/peso, empurrando a ação principal (registrar a série)
                                  pra baixo e competindo por atenção com ela logo na abertura
                                  do card. Reps/peso agora vêm logo depois do resumo curto;
                                  as instruções (e a duração já registrada, quando existe)
                                  ficam disponíveis abaixo, pra quem quiser consultá-las, sem
                                  bloquear o caminho principal. */}
                              <div className={styles.workoutSummaryFooter} onClick={(e) => e.stopPropagation()}>
                                {/* Active session with sets: show sets tracker */}
                                {workoutSession && !workoutModalReadonly && !isLocked && workoutSession.assignmentId === selectedWorkoutAssignment?.id && item.sets_planned > 0 ? (
                                  (() => {
                                    const setsData = (workoutSession.setsLog || {})[item.id];
                                    // Still initializing — show loading placeholder
                                    if (!setsData || setsData.length === 0) {
                                      return <div style={{ fontSize: 12, color: 'var(--text-0-40)', padding: '8px 0' }}>Carregando séries…</div>;
                                    }
                                    const exerciseIndex = index;
                                    return (
                                      <WorkoutSetsTracker
                                        item={item}
                                        setsData={setsData}
                                        isDone={isDone}
                                        onSetUpdate={(setIdx, patch) => updateSetLog(item.id, setIdx, patch)}
                                        onComplete={(finalSets) => completeExercise(item, itemsOrdered, finalSets)}
                                        onRestStart={(secs, name, itemId) => setActiveRest({ seconds: secs, exerciseName: name, itemId, itemIds: null })}
                                        onRestEnd={() => setActiveRest(null)}
                                        isResting={isRestingThis}
                                        previousSets={previousSets[String(item.id)]}
                                        onError={(msg) => toastApi.error(msg)}
                                        onSuccess={(msg) => toastApi.success(msg, 3000, customColor)}
                                        exerciseIndex={exerciseIndex}
                                        totalExercises={totalExercises}
                                        completedSoFar={completedSoFar}
                                      />
                                    );
                                  })()
                                ) : isLocked ? (
                                  /* Locked: another exercise is in progress */
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 0', fontSize: 12, color: 'var(--text-0-40)' }}>
                                    <span style={{ fontSize: 14 }}><FontAwesomeIcon icon={faLock} /></span>
                                    <span>Conclua o exercício em andamento primeiro</span>
                                  </div>
                                ) : (workoutSession && !workoutModalReadonly && !isDone && !(item.sets_planned > 0) && workoutSession.assignmentId === selectedWorkoutAssignment?.id) ? (
                                  /* Exercício sem séries (cardio, mobilidade, etc.) — botão de conclusão simples */
                                  <button
                                    style={{
                                      marginTop: 8, padding: '8px 16px', borderRadius: 10,
                                      background: 'rgba(var(--accent-rgb),0.15)', border: '1px solid rgba(var(--accent-rgb),0.35)',
                                      color: 'var(--blue)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                                      display: 'flex', alignItems: 'center', gap: 6, transition: 'background .15s',
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(var(--accent-rgb),0.28)'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(var(--accent-rgb),0.15)'}
                                    onClick={() => completeExercise(item, itemsOrdered)}
                                  >
                                    <FontAwesomeIcon icon={faCheckCircle} />
                                    Concluir exercício
                                  </button>
                                ) : null}
                              </div>

                              {(() => {
                                const notes = (item?.notes || "").replace(/[—–]/g, "-");
                                const NOTE_LIMIT = 110;
                                const isLong = notes.length > NOTE_LIMIT;
                                const isExpanded = expandedNotes.has(item.id);
                                if (!notes) return null;
                                return (
                                  <div className={styles.workoutSummaryNote}>
                                    {isLong && !isExpanded ? notes.slice(0, NOTE_LIMIT).trimEnd() + "…" : notes}
                                    {isLong && (
                                      <button
                                        type="button"
                                        className={styles.notesToggle}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setExpandedNotes(prev => {
                                            const next = new Set(prev);
                                            isExpanded ? next.delete(item.id) : next.add(item.id);
                                            return next;
                                          });
                                        }}
                                      >
                                        {isExpanded ? "ver menos" : "ver mais"}
                                      </button>
                                    )}
                                  </div>
                                );
                              })()}
                              {workoutSession?.exerciseDurations?.[item.id] && (
                                <div className={styles.workoutSummaryNote} style={{ fontSize: 12, color: '#6b7280' }}>
                                  Duração registrada: {workoutSession.exerciseDurations[item.id]} min
                                </div>
                              )}
                            </div>
                          );
                        }

                        // ── Bloco de grupo (bi-set/superset/circuito) ──
                        const groupLabel = computeGroupLabel(blockItems);
                        const groupNames = blockItems.map((it) => it.exercise_item?.name || `Item ${it.id}`);

                        if (isDone && !isRestingThis) {
                          const completedSets = blockItems.reduce((acc, it) => acc + (workoutSession?.setsLog?.[it.id] || []).filter(s => s.done).length, 0);
                          return (
                            <div
                              key={blockKey}
                              className={styles.workoutSummaryCardCollapsed}
                              role="button"
                              aria-label={`${groupLabel}: ${groupNames.join(" + ")} — concluído`}
                              onClick={() => blockItems[0]?.exercise_item && setDemoExerciseItem(blockItems[0].exercise_item)}
                            >
                              <div className={styles.collapsedRow}>
                                <FontAwesomeIcon icon={faCheckCircle} className={styles.doneIcon} />
                                <span className={styles.collapsedName}>{groupLabel}: {groupNames.join(" + ")}</span>
                                <span className={styles.collapsedMeta}>
                                  {completedSets > 0 ? `${completedSets} séries` : "concluído"}
                                </span>
                              </div>
                            </div>
                          );
                        }

                        const setsDataByItemId = {};
                        const previousSetsByItemId = {};
                        blockItems.forEach((it) => {
                          setsDataByItemId[it.id] = (workoutSession?.setsLog || {})[it.id] || [];
                          previousSetsByItemId[it.id] = previousSets[String(it.id)];
                        });
                        const stillLoading = blockItems.some((it) => !setsDataByItemId[it.id] || setsDataByItemId[it.id].length === 0);
                        const groupExerciseIndex = itemsOrdered.findIndex((x) => x.id === blockItems[0].id);

                        return (
                          <div
                            key={blockKey}
                            className={cn(styles.workoutSummaryCard, styles.workoutSummaryCardGroup, isLocked && styles.workoutSummaryCardLocked)}
                          >
                            <div className={styles.workoutSummaryTop}>
                              <div className={styles.workoutSummaryLabel}>
                                <FontAwesomeIcon icon={faLink} />
                                {groupLabel} · sem descanso entre os exercícios
                              </div>
                            </div>
                            <div className={styles.workoutSummaryTitle}>
                              {blockItems.map((it, i) => (
                                <div key={it.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: i > 0 ? 4 : 0 }}>
                                  <span>{i + 1}. {it.exercise_item?.name || `Item ${it.id}`}</span>
                                  {it.exercise_item && (
                                    <button
                                      type="button"
                                      className={styles.demoBtnPulse}
                                      onClick={() => setDemoExerciseItem(it.exercise_item)}
                                      aria-label={`Ver demonstração de ${it.exercise_item?.name || "exercício"}`}
                                    >
                                      <FontAwesomeIcon icon={faPlay} />
                                      Demo
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>

                            {/* onClick stopPropagation: mesmo padrao do bloco de item
                                avulso acima (linha ~3880) -- ainda nao ha onClick no
                                card de grupo hoje, mas os botoes internos (SetRow/
                                TimedSetRow, "Pular descanso") dependem inteiramente
                                deste wrapper pra nao borbulhar; deixar isso consistente
                                entre os dois blocos evita que o card de grupo herde
                                silenciosamente um bug de clique se um onClick for
                                adicionado aqui no futuro (achado de auditoria, 18/09/2026). */}
                            <div className={styles.workoutSummaryFooter} onClick={(e) => e.stopPropagation()}>
                              {workoutSession && !workoutModalReadonly && !isLocked && workoutSession.assignmentId === selectedWorkoutAssignment?.id ? (
                                stillLoading ? (
                                  <div style={{ fontSize: 12, color: 'var(--text-0-40)', padding: '8px 0' }}>Carregando séries…</div>
                                ) : (
                                  <GroupSetsTracker
                                    items={blockItems}
                                    setsDataByItemId={setsDataByItemId}
                                    doneMap={workoutSession?.exerciseCompletions || {}}
                                    onSetUpdate={(itemId, setIdx, patch) => updateSetLog(itemId, setIdx, patch)}
                                    onItemComplete={(it, finalSets) => completeExercise(it, itemsOrdered, finalSets)}
                                    onRestStart={(secs, name, itemIds) => setActiveRest({ seconds: secs, exerciseName: name, itemId: null, itemIds })}
                                    onRestEnd={() => setActiveRest(null)}
                                    isResting={isRestingThis}
                                    previousSetsByItemId={previousSetsByItemId}
                                    onError={(msg) => toastApi.error(msg)}
                                    onSuccess={(msg) => toastApi.success(msg, 3000, customColor)}
                                    exerciseIndex={groupExerciseIndex}
                                    totalExercises={totalExercises}
                                    completedSoFar={completedSoFar}
                                  />
                                )
                              ) : isLocked ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 0', fontSize: 12, color: 'var(--text-0-40)' }}>
                                  <span style={{ fontSize: 14 }}><FontAwesomeIcon icon={faLock} /></span>
                                  <span>Conclua o exercício em andamento primeiro</span>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>

                </div>
              )}

              {!assignedWorkouts.length && (
                <div className={cn(styles.pill, styles.pillBad)}>
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                  <span>Você ainda não tem treinos relacionados. Peça ao professor para vincular um treino ao seu perfil.</span>
                </div>
              )}

              <div className={styles.field}>
                <label className={styles.label} htmlFor="checkin-duration-desktop">2. Duração do treino (minutos)</label>
                <input
                  id="checkin-duration-desktop"
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9]*[.,]?[0-9]*"
                  min={1}
                  step="5"
                  value={
                    (workoutSession && allExercisesCompleted(selectedWorkoutAssignment))
                      ? String(Math.round(elapsedSeconds / 60) || "")
                      : form.duration
                  }
                  readOnly={!!(workoutSession && allExercisesCompleted(selectedWorkoutAssignment))}
                  onChange={(e) => setForm({ ...form, duration: e.target.value })}
                  placeholder="Informe quanto tempo treinou"
                  className={styles.input}
                />
              </div>

              {photoRequiredNow && (
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="checkin-photo-desktop">
                    3. Foto (obrigatória — desafio com registro fotográfico)
                  </label>
                  <input
                    id="checkin-photo-desktop"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => setForm({ ...form, photo: e.target.files?.[0] || null })}
                    className={styles.input}
                  />
                  {form.photo && (
                    <span style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 4, display: "block" }}>
                      {form.photo.name}
                    </span>
                  )}
                </div>
              )}

              {/* pill de duração removido — pontuação agora é por volume, não tempo */}
            </>
        </Modal>
      )}


      <ConfirmDialog
        open={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        confirmLabel={confirmState.confirmLabel}
        cancelLabel={confirmState.cancelLabel}
        confirmIcon={confirmState.confirmIcon}
        cancelIcon={confirmState.cancelIcon}
        icon={confirmState.icon}
        confirmVariant={confirmState.confirmVariant}
        onDismiss={() => { closeConfirm(); setPendingViewAssignmentId(null); }}
        onClose={() => {
          closeConfirm();
          // se cancelou e havia um treino pendente para visualizar, abre em modo leitura
          if (pendingViewAssignmentId && !workoutSession) {
            setSelectedWorkoutAssignmentId(String(pendingViewAssignmentId));
            setWorkoutModalPreStart(false);
            setWorkoutModalReadonly(true);
            setShowCheckinModal(true);
            setPendingViewAssignmentId(null);
          }
        }}
        onConfirm={runConfirm}
      />

      {confirmDialog && (
        <Modal
          onClose={() => setConfirmDialog(null)}
          onConfirm={async () => {
            await confirmDialog.onConfirm?.();
            setConfirmDialog(null);
          }}
          confirmLabel={confirmDialog.confirmLabel || "Confirmar"}
          isMobile={isMobile}
          title={confirmDialog.title}
        >
          <p className={styles.muted}>{confirmDialog.message}</p>
        </Modal>
      )}

      {/* Toast local removido: usamos ToastProvider global */}

      {demoExerciseItem && (
        <ExerciseDemoModal
          open={!!demoExerciseItem}
          exerciseItem={demoExerciseItem}
          onClose={() => setDemoExerciseItem(null)}
        />
      )}

      {openStudentReport && (
        <StudentReportModal
          isSelfService={!me?.professor_id} onClose={() => setOpenStudentReport(false)} />
      )}

      {rationaleModal.open && (
        <WorkoutRationaleModal
          rationale={rationaleModal.text}
          onClose={() => setRationaleModal({ open: false, text: null })}
          professorLogoUrl={me?.professor_logo_url}
        />
      )}

      {/* SelfWorkoutModal para alunos (regerar treino pelo menu) */}
      {me?.role === "aluno" && (
        <SelfWorkoutModal
          open={openSelfWorkout}
          onClose={() => setOpenSelfWorkout(false)}
          onSuccess={(freshProfile) => { if (freshProfile) onUserUpdate?.(freshProfile); loadData(); }}
          onOpenSupport={() => { setOpenSelfWorkout(false); setTimeout(() => setOpenStudentReport(true), 200); }}
          onOpenPayments={() => { setOpenSelfWorkout(false); setTimeout(() => setShowPaymentsModal(true), 200); }}
          user={me}
        />
      )}

      {/* Modal de cooldown — geração de treino por IA (1x/mês) */}
      {openWorkoutCooldown && createPortal(
        <div
          onClick={() => setOpenWorkoutCooldown(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 1200,
            background: "var(--border-0-65)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "1rem",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "var(--surface-solid)",
              border: "1px solid var(--border-0-15)",
              borderRadius: 16, padding: "28px 28px 24px",
              maxWidth: 420, width: "100%",
              boxShadow: "0 24px 60px rgba(0,0,0,0.5)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <span style={{
                width: 36, height: 36, borderRadius: "50%",
                background: "var(--accent-0-15)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "var(--accent)", fontSize: 16, flexShrink: 0,
              }}>
                <FontAwesomeIcon icon={faWandMagicSparkles} />
              </span>
              <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: "var(--text, #f1f5f9)" }}>
                Geração disponível em breve
              </h3>
            </div>
            <p style={{ color: "var(--muted, #94a3b8)", fontSize: "0.88rem", lineHeight: 1.65, margin: "0 0 12px" }}>
              Você já gerou um treino com IA em{" "}
              <strong style={{ color: "var(--text, #f1f5f9)" }}>
                {workoutCooldownInfo?.last_generated_at
                  ? new Date(workoutCooldownInfo.last_generated_at).toLocaleDateString("pt-BR")
                  : "data indisponível"}
              </strong>.
              A renovação é liberada <strong style={{ color: "var(--text, #f1f5f9)" }}>uma vez a cada 90 dias</strong> - esse é o intervalo recomendado pela ciência do treinamento.
            </p>

            <div style={{
              background: "rgba(var(--accent-rgb),0.07)", border: "1px solid rgba(var(--accent-rgb),0.2)",
              borderRadius: 10, padding: "12px 14px", margin: "0 0 14px",
              fontSize: "0.82rem", color: "var(--muted, #94a3b8)", lineHeight: 1.7,
            }}>
              <strong style={{ color: "var(--accent, #1d9e75)", display: "block", marginBottom: 6 }}>
                Por que 3 meses?
              </strong>
              <ul style={{ margin: 0, paddingLeft: 16, display: "flex", flexDirection: "column", gap: 4 }}>
                <li><strong style={{ color: "var(--text, #f1f5f9)" }}>Semanas 1-4:</strong> adaptação neural - seu sistema nervoso aprende os movimentos.</li>
                <li><strong style={{ color: "var(--text, #f1f5f9)" }}>Semanas 5-8:</strong> hipertrofia real - fibras musculares crescem e ficam mais fortes.</li>
                <li><strong style={{ color: "var(--text, #f1f5f9)" }}>Semanas 9-12:</strong> consolidação - volume e carga se acumulam, resultados aparecem.</li>
              </ul>
              <p style={{ margin: "8px 0 0", fontSize: "0.8rem" }}>
                Trocar antes disso interrompe o ciclo e você perde os ganhos que estavam vindo.
              </p>
            </div>

            <p style={{ color: "var(--muted, #94a3b8)", fontSize: "0.85rem", lineHeight: 1.65, margin: "0 0 24px",
              background: "var(--border-0-06)", border: "1px solid var(--border-0-08)",
              borderRadius: 10, padding: "10px 14px" }}>
              Seu próximo treino poderá ser gerado em{" "}
              <strong style={{ color: "var(--accent, #1d9e75)" }}>
                {workoutCooldownInfo?.next_allowed_at
                  ? new Date(workoutCooldownInfo.next_allowed_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })
                  : "breve"}
              </strong>.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => setOpenWorkoutCooldown(false)}>
                Entendi
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de instalação do PWA — pré-requisito pra notificação push */}
      <InstallPwaModal
        open={openInstallPwa}
        onClose={() => setOpenInstallPwa(false)}
        platform={pwa.platform}
        browser={pwa.browser}
        canPromptInstall={pwa.canPromptInstall}
        onPromptInstall={pwa.promptInstall}
      />

      {/* Convite para ativar as notificações (app já instalado, permissão ainda indefinida) */}
      <EnablePushPrompt
        open={openEnablePush}
        onEnable={handleEnablePushFromPrompt}
        onDismiss={handleDismissPushPrompt}
      />

      {/* Modal de aviso de renovação — treino vencido há mais de 90 dias (self-service) */}
      {openRenewalReminder && createPortal(
        <div
          onClick={() => setOpenRenewalReminder(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 1200,
            background: "var(--border-0-65)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "1rem",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "var(--surface-solid)",
              border: "1px solid var(--border-0-15)",
              borderRadius: 16, padding: "28px 28px 24px",
              maxWidth: 420, width: "100%",
              boxShadow: "0 24px 60px rgba(0,0,0,0.5)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <span style={{
                width: 36, height: 36, borderRadius: "50%",
                background: "rgba(var(--amber-rgb),0.18)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "var(--amber)", fontSize: 16, flexShrink: 0,
              }}>
                <FontAwesomeIcon icon={faTriangleExclamation} />
              </span>
              <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: "var(--text, #f1f5f9)" }}>
                Seu treino está vencido
              </h3>
            </div>
            <p style={{ color: "var(--muted, #94a3b8)", fontSize: "0.88rem", lineHeight: 1.65, margin: "0 0 12px" }}>
              Seu treino foi gerado em{" "}
              <strong style={{ color: "var(--text, #f1f5f9)" }}>
                {renewalInfo?.last_generated_at
                  ? new Date(renewalInfo.last_generated_at).toLocaleDateString("pt-BR")
                  : "data indisponível"}
              </strong>{" "}
              e o prazo de <strong style={{ color: "var(--text, #f1f5f9)" }}>90 dias</strong> recomendado venceu em{" "}
              <strong style={{ color: "var(--amber)" }}>
                {renewalInfo?.next_allowed_at
                  ? new Date(renewalInfo.next_allowed_at).toLocaleDateString("pt-BR")
                  : "data indisponível"}
              </strong>
              {renewalInfo?.next_allowed_at && (() => {
                const overdueDays = Math.max(0, Math.floor((Date.now() - new Date(renewalInfo.next_allowed_at).getTime()) / 86400000));
                return overdueDays > 0 ? (
                  <> — já são <strong style={{ color: "var(--text, #f1f5f9)" }}>{overdueDays} {overdueDays === 1 ? "dia" : "dias"}</strong> além do prazo</>
                ) : null;
              })()}
              . Ficar tanto tempo sem renovar reduz o estímulo de progressão do seu treino.
            </p>

            <div style={{
              background: "rgba(var(--amber-rgb),0.07)", border: "1px solid rgba(var(--amber-rgb),0.2)",
              borderRadius: 10, padding: "12px 14px", margin: "0 0 14px",
              fontSize: "0.82rem", color: "var(--muted, #94a3b8)", lineHeight: 1.7,
            }}>
              <strong style={{ color: "var(--amber)", display: "block", marginBottom: 6 }}>
                Por que renovar agora?
              </strong>
              <ul style={{ margin: 0, paddingLeft: 16, display: "flex", flexDirection: "column", gap: 4 }}>
                <li>Seu corpo já se adaptou aos estímulos atuais - sem novidade, os ganhos estagnam.</li>
                <li>A renovação recalcula o treino com base no seu progresso e nível mais recentes.</li>
                <li>Treinar vencido por muito tempo aumenta o risco de platô e desmotivação.</li>
              </ul>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button className={styles.btn} onClick={() => setOpenRenewalReminder(false)}>
                Depois
              </button>
              <button
                className={`${styles.btn} ${styles.btnPrimary}`}
                onClick={() => { setOpenRenewalReminder(false); setOpenSelfWorkout(true); }}
              >
                Renovar agora
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ===== BOTTOM NAVIGATION (mobile only) ===== */}
      {isMobile && (
        <nav className={styles.bottomNav} aria-label="Navegação principal">
          {/* Estado ativo: seção visível na tela, ou o modal aberto no
              momento (Cardio/Nutrição são destinos do ponto de vista do
              usuário, mesmo sendo modal). aria-current="page" leva a mesma
              informação pro leitor de tela — cor sozinha não basta. */}
          {/* Início — scroll to top */}
          <button
            type="button"
            className={cn(styles.bottomNavItem, navActive === "inicio" && styles.bottomNavItemActive)}
            aria-current={navActive === "inicio" ? "page" : undefined}
            aria-label="Início"
            onClick={() => {
              setActiveTopTab('overview');
              // iOS Safari ignores window.scrollTo in some cases — target both
              try {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
                document.body.scrollTop = 0;
              } catch (_) {
                window.scrollTo(0, 0);
              }
            }}
          >
            <span className={styles.bottomNavItemIcon}>
              <FontAwesomeIcon icon={faArrowTrendUp} />
            </span>
            <span className={styles.bottomNavItemLabel}>Início</span>
          </button>

          {/* Treinos — scroll to workout section */}
          <button
            type="button"
            className={cn(styles.bottomNavItem, navActive === "treinos" && styles.bottomNavItemActive)}
            aria-current={navActive === "treinos" ? "page" : undefined}
            aria-label="Treinos"
            onClick={() => {
              setActiveTopTab('overview');
              setTimeout(() => workoutSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
            }}
          >
            <span className={styles.bottomNavItemIcon}>
              <FontAwesomeIcon icon={faDumbbell} />
            </span>
            <span className={styles.bottomNavItemLabel}>Treinos</span>
          </button>

          {/* Cardio */}
          <button
            type="button"
            className={cn(styles.bottomNavItem, navActive === "cardio" && styles.bottomNavItemActive)}
            aria-current={navActive === "cardio" ? "page" : undefined}
            aria-label="Cardio"
            onClick={() => setShowCardioModal(true)}
          >
            <span className={styles.bottomNavItemIcon}>
              <FontAwesomeIcon icon={faPersonRunning} />
            </span>
            <span className={styles.bottomNavItemLabel}>Cardio</span>
          </button>

          {/* Centre CTA — Nutrição: apenas para alunos com dieta */}
          {me?.role === "aluno" && hasDiet && (!me?.is_self_service || hasPaidAccess) ? (
            <button
              type="button"
              className={cn(styles.bottomNavItem, navActive === "nutricao" && styles.bottomNavItemActive)}
              aria-current={navActive === "nutricao" ? "page" : undefined}
              aria-label="Nutrição"
              onClick={() => setOpenNutrition(true)}
            >
              <span className={styles.bottomNavItemIcon}>
                <FontAwesomeIcon icon={faUtensils} />
              </span>
              <span className={styles.bottomNavItemLabel}>Nutrição</span>
            </button>
          ) : null}

          {/* Evolução — scroll to evolution section */}
          <button
            type="button"
            className={cn(styles.bottomNavItem, navActive === "evolucao" && styles.bottomNavItemActive)}
            aria-current={navActive === "evolucao" ? "page" : undefined}
            aria-label="Evolução"
            onClick={() => {
              setActiveTopTab('overview');
              setTimeout(() => evolutionSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
            }}
          >
            <span className={styles.bottomNavItemIcon}>
              <FontAwesomeIcon icon={faChartLine} />
            </span>
            <span className={styles.bottomNavItemLabel}>Evolução</span>
          </button>

          {/* Menu — abre bottom sheet */}
          <button
            type="button"
            className={cn(styles.bottomNavItem, navActive === "menu" && styles.bottomNavItemActive)}
            aria-current={navActive === "menu" ? "page" : undefined}
            aria-label="Menu"
            onClick={() => setShowMenu(true)}
          >
            <span className={styles.bottomNavItemIcon}>
              <FontAwesomeIcon icon={faBars} />
            </span>
            <span className={styles.bottomNavItemLabel}>Menu</span>
          </button>
        </nav>
      )}
    </div>
  </LogoContext.Provider>
  );
}
